/**
 * Comprehensive SDK Test Page
 * Tests all available APIs from the SDK:
 * - ExchangeClient (trading operations)
 * - InfoClient (market data)
 * - Utility functions (satoshi conversion)
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import {
  ExchangeClient,
  InfoClient,
  SubscriptionClient,
  toSatoshi,
  fromSatoshi,
  toSatoshiUint64,
  isValidSatoshi,
  toSatoshiBatch,
  fromSatoshiBatch,
  SATOSHI_SCALE,
  mormSigner,
} from "../sdk";
import { shouldRefreshNonce } from "../sdk/utils/errorDetection";
import { executeWithRetry, RetryConfig } from "../sdk/utils/retry";
import { int64ToHex, toNumberFromSatoshi } from "../sdk/utils/math";
import { formatPriceFromSatoshi, formatLeverage } from "../sdk/utils/display";
import {
  CURRENCY_MIN_FRACTION_DIGITS,
  CURRENCY_MAX_FRACTION_DIGITS,
  DEFAULT_MNEMONIC,
} from "../components/smartlayout/constants";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { useBuckets } from "../hooks/useBuckets";
import { DEFAULT_BASE_URL, DEFAULT_DEVICE_ID, DEFAULT_WS_URL } from "../configs/base";
import {
  RunAllTestsButton,
  OnboardButton,
  UtilityTestButton,
  InfoClientTestButton,
  WalletSpecificTestButton,
  ExchangeClientTestButton,
  ClearResultsButton,
  DismissErrorButton,
} from "./sdk_test_page/SDKTestPageButtons";
import { TestResultItem, TestResult } from "./sdk_test_page/TestResultItem";
import { SectionHeader } from "./sdk_test_page/SectionHeader";
import "./sdk_test_page/sdktest.css";

interface TestSection {
  name: string;
  results: TestResult[];
}

export default function SDKTestPage() {
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [marketId, setMarketId] = useState<string>("");
  const [sessionToken, setSessionToken] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);
  const [sections, setSections] = useState<Record<string, TestSection>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [isAuthTestRunning, setIsAuthTestRunning] = useState(false);
  const [isUtilityTestRunning, setIsUtilityTestRunning] = useState(false);
  const [isInfoClientTestRunning, setIsInfoClientTestRunning] = useState(false);
  const [isWalletSpecificTestRunning, setIsWalletSpecificTestRunning] = useState(false);
  const [isExchangeClientTestRunning, setIsExchangeClientTestRunning] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  const exchangeClientRef = useRef<ExchangeClient | null>(null);
  const infoClientRef = useRef<InfoClient | null>(null);
  const baseURLRef = useRef<string>(baseURL);
  const fetchedMarketsRef = useRef<any[]>([]); // Store fetched markets immediately
  const fetchedMarketIdRef = useRef<string>(""); // Store fetched market ID immediately

  // Use markets hook to manage market data retention
  const { markets, fetchMarkets } = useMarkets({
    baseURL,
    sessionToken,
    autoFetchMarkets: false, // We'll fetch manually in testInfoClient
  });

  // Use onboarding hook as single source of truth for account information
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    loading: onboardLoading,
    error: onboardError,
    onboard: performOnboard,
    clearError: clearOnboardError,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  // Initialize mnemonic with default value if not set
  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(DEFAULT_MNEMONIC );
    }
  }, [mnemonic, setOnboardMnemonic]);

  // Update mnemonic ref when mnemonic changes
  const mnemonicRef = useRef<string>(mnemonic || "");
  useEffect(() => {
    mnemonicRef.current = mnemonic || "";
  }, [mnemonic]);

  // Create subscription client instance for buckets hook
  const subscriptionClient = useMemo(
    () =>
      new SubscriptionClient({
        url: DEFAULT_WS_URL,
        authToken: sessionToken || authToken || undefined,
        walletAddress: walletAddress || undefined,
        deviceID: deviceID || undefined,
      }),
    [sessionToken, authToken, walletAddress, deviceID],
  );

  // Use buckets hook to manage bucket data
  const {
    buckets: hookBuckets,
    loading: bucketsLoading,
    error: bucketsError,
    refresh: refreshBuckets,
  } = useBuckets({
    subscriptionClient,
    baseURL,
    authToken: sessionToken || authToken,
    walletAddress: walletAddress,
    refreshInterval: 0, // Disable auto-refresh for test page
    useWebSocket: false, // Disable WebSocket for test page
    marketId: "0", // Default to user-global market
  });

  // Sync onboard auth token to local state when it changes
  useEffect(() => {
    if (onboardAuthToken) {
      setAuthToken(onboardAuthToken);
      // Store in localStorage for sharing with other pages
      localStorage.setItem("authToken", onboardAuthToken);
    }
  }, [onboardAuthToken]);

  // Sync authToken state to localStorage whenever it changes
  useEffect(() => {
    if (authToken && authToken.trim() !== "") {
      localStorage.setItem("authToken", authToken);
    }
  }, [authToken]);

  // Show test completion toast when tests finish
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    // When isRunning changes from true to false, tests just completed
    if (prevIsRunningRef.current === true && isRunning === false) {
      // Wait a bit for state to update, then calculate results
      setTimeout(() => {
        const allResults = Object.values(sections).flatMap((s) => s.results);
        const successCount = allResults.filter((r) => r.success).length;
        const totalCount = allResults.length;
        const failureCount = totalCount - successCount;

        if (totalCount > 0) {
          if (failureCount === 0) {
            toast.success("All tests passed", {
              description: `${totalCount} test(s) completed successfully.`,
              duration: 5000,
            });
          } else {
            toast.warning("Tests completed with failures", {
              description: `${successCount}/${totalCount} tests passed. ${failureCount} test(s) failed.`,
              duration: 6000,
            });
          }
        }
      }, 100);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, sections]);

  // Load authToken from localStorage on mount
  useEffect(() => {
    const storedAuthToken = localStorage.getItem("authToken");
    if (storedAuthToken && !authToken) {
      setAuthToken(storedAuthToken);
    }
  }, []);

  // Compute default market ID (first active market, or first in list)
  // Use market_id (string) from API, fallback to id (number) converted to string
  const defaultMarketId =
    markets.length > 0
      ? (markets.find((m) => m.active)?.market_id ?? markets[0].market_id) ||
        (markets.find((m) => m.active)?.id?.toString() ??
          markets[0].id?.toString() ??
          "")
      : null;

  // Use defaultMarketId if available, fallback to user input
  const effectiveMarketId = defaultMarketId ?? marketId;

  // Update marketId state when default changes (for UI consistency)
  useEffect(() => {
    if (defaultMarketId && marketId !== defaultMarketId) {
      setMarketId(defaultMarketId);
    }
  }, [defaultMarketId]);

  // Update refs when values change
  useEffect(() => {
    baseURLRef.current = baseURL;
  }, [baseURL]);

  const addResult = (section: string, result: TestResult) => {
    setSections((prev) => {
      const sectionData = prev[section] || { name: section, results: [] };
      return {
        ...prev,
        [section]: {
          ...sectionData,
          results: [...sectionData.results, result],
        },
      };
    });
  };

  const clearResults = () => {
    setSections({});
    setExpandedSections(new Set());
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  // Auto-expand sections when they first get results
  useEffect(() => {
    const newSections = Object.keys(sections).filter(
      (name) => sections[name].results.length > 0,
    );
    if (newSections.length > 0) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        newSections.forEach((name) => next.add(name));
        return next;
      });
    }
  }, [sections]);

  // Utility Functions Tests
  const testUtilities = () => {
    const section = "Utilities";
    addResult(section, {
      success: true,
      message: "Testing utility functions...",
      timestamp: Date.now(),
    });

    try {
      // Test toSatoshi
      const satoshi1 = toSatoshi(1.0);
      addResult(section, {
        success: satoshi1 === "100000000",
        message: `toSatoshi(1.0) = ${satoshi1}`,
        data: { expected: "100000000", got: satoshi1 },
        timestamp: Date.now(),
      });

      const satoshi2 = toSatoshi("1.5");
      addResult(section, {
        success: satoshi2 === "150000000",
        message: `toSatoshi("1.5") = ${satoshi2}`,
        data: { expected: "150000000", got: satoshi2 },
        timestamp: Date.now(),
      });

      // Test fromSatoshi
      const decimal1 = fromSatoshi("100000000");
      addResult(section, {
        success: decimal1 === 1.0,
        message: `fromSatoshi("100000000") = ${decimal1}`,
        data: { expected: 1.0, got: decimal1 },
        timestamp: Date.now(),
      });

      // Test fromSatoshi with large value (precision test)
      const largeSatoshi = "64999999999986700000000";
      const decimalLarge = fromSatoshi(largeSatoshi);
      const expectedLarge = 649999999999867;
      const isPrecise = decimalLarge === expectedLarge;
      addResult(section, {
        success: isPrecise,
        message: `fromSatoshi("${largeSatoshi}") = ${decimalLarge}`,
        data: { 
          expected: expectedLarge, 
          got: decimalLarge,
          difference: Math.abs(decimalLarge - expectedLarge),
          isPrecise 
        },
        timestamp: Date.now(),
      });

      // Test toNumberFromSatoshi with large value
      const toNumberLargeSatoshi = "64999999999986700000000";
      const toNumberDecimalLarge = toNumberFromSatoshi(toNumberLargeSatoshi);
      const toNumberExpectedLarge = 649999999999867;
      const toNumberIsPrecise = toNumberDecimalLarge === toNumberExpectedLarge;
      addResult(section, {
        success: toNumberIsPrecise,
        message: `toNumberFromSatoshi("${toNumberLargeSatoshi}") = ${toNumberDecimalLarge}`,
        data: { 
          expected: toNumberExpectedLarge, 
          got: toNumberDecimalLarge,
          difference: Math.abs(toNumberDecimalLarge - toNumberExpectedLarge),
          isPrecise: toNumberIsPrecise 
        },
        timestamp: Date.now(),
      });

      // Test toSatoshiUint64
      const uint64 = toSatoshiUint64(1.0);
      addResult(section, {
        success: uint64 === 100000000,
        message: `toSatoshiUint64(1.0) = ${uint64}`,
        data: { expected: 100000000, got: uint64 },
        timestamp: Date.now(),
      });

      // Test isValidSatoshi
      const valid1 = isValidSatoshi("100000000");
      const valid2 = isValidSatoshi("abc");
      addResult(section, {
        success: valid1 && !valid2,
        message: `isValidSatoshi tests: "100000000"=${valid1}, "abc"=${valid2}`,
        data: { valid1, valid2 },
        timestamp: Date.now(),
      });

      // Test batch functions
      const batchInput = [1.0, 1.5, 2.0];
      const batchSatoshi = toSatoshiBatch(batchInput);
      const batchDecimal = fromSatoshiBatch(batchSatoshi);
      addResult(section, {
        success: batchDecimal.length === 3,
        message: `Batch conversion: ${batchInput.length} items`,
        data: {
          input: batchInput,
          satoshi: batchSatoshi,
          decimal: batchDecimal,
        },
        timestamp: Date.now(),
      });

      // Test SATOSHI_SCALE constant
      addResult(section, {
        success: SATOSHI_SCALE === 100000000,
        message: `SATOSHI_SCALE = ${SATOSHI_SCALE}`,
        data: { expected: 100000000, got: SATOSHI_SCALE },
        timestamp: Date.now(),
      });

      // Test int64ToHex with large value
      const largeValue = "64999999999986700000000";
      const hexResult = int64ToHex(largeValue);
      addResult(section, {
        success: hexResult.startsWith("0x"),
        message: `int64ToHex("${largeValue}") = ${hexResult}`,
        data: { 
          input: largeValue, 
          output: hexResult,
          isValidHex: hexResult.startsWith("0x")
        },
        timestamp: Date.now(),
      });

      // Test formatPriceFromSatoshi with large value
      const largePriceValue = "64999999999986700000000";
      const formattedPrice = formatPriceFromSatoshi(largePriceValue);
      addResult(section, {
        success: formattedPrice !== "--" && formattedPrice.length > 0,
        message: `formatPriceFromSatoshi("${largePriceValue}") = ${formattedPrice}`,
        data: { 
          input: largePriceValue, 
          output: formattedPrice,
          isValid: formattedPrice !== "--" && formattedPrice.length > 0
        },
        timestamp: Date.now(),
      });

      // Test formatLeverage with string value
      const leverageValue = "86755123000";
      const formattedLeverage = formatLeverage(leverageValue);
      addResult(section, {
        success: formattedLeverage !== "--" && formattedLeverage.length > 0,
        message: `formatLeverage("${leverageValue}") = ${formattedLeverage}`,
        data: { 
          input: leverageValue, 
          output: formattedLeverage,
          isValid: formattedLeverage !== "--" && formattedLeverage.length > 0
        },
        timestamp: Date.now(),
      });

      // Test toLocaleString with currency format options
      const number_x = 649999999999867;
      const currencyFormatOptions = {
        minimumFractionDigits: CURRENCY_MIN_FRACTION_DIGITS,
        maximumFractionDigits: CURRENCY_MAX_FRACTION_DIGITS,
      };
      const formattedCurrency = number_x.toLocaleString("en-US", currencyFormatOptions);
      addResult(section, {
        success: formattedCurrency.length > 0 && formattedCurrency.includes("649"),
        message: `number_x.toLocaleString("en-US", currencyFormatOptions) = ${formattedCurrency}`,
        data: { 
          input: number_x, 
          output: formattedCurrency,
          currencyFormatOptions,
          isValid: formattedCurrency.length > 0
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Utility test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // Authentication Test Section
  const testAuthentication = async (section: string = "Authentication") => {
    if (!walletAddress || walletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Authentication skipped: Wallet address required",
        timestamp: Date.now(),
      });
      return;
    }

    // Check if mormSigner is available for address generation (still needed)
    // Note: Actual signing now uses ethers.js, but we need WASM for address derivation
    if (!mormSigner.isReady()) {
      addResult(section, {
        success: false,
        message:
          "Authentication failed: mormSigner not loaded. Please initialize mormSigner first for address generation.",
        timestamp: Date.now(),
      });
      return;
    }

    try {
      addResult(section, {
        success: true,
        message:
          "Starting authentication (onboarding) with ethers.js EIP-712 signing...",
        timestamp: Date.now(),
      });

      // Use the refactored onboarding function
      // This uses ethers.js for EIP-712 signing (via signing/onboard.ts)
      // onboardAndGetAuthToken handles: nonce fetching, message building, signing, and HTTP request
      const { onboardAndGetAuthToken } = await import("../sdk/utils/onboard");

      addResult(section, {
        success: true,
        message:
          "Calling onboardAndGetAuthToken (handles nonce, EIP-712 signing with ethers.js, and API request)...",
        timestamp: Date.now(),
      });

      if (!mnemonic) {
        throw new Error("Mnemonic is required for authentication");
      }

      const result = await onboardAndGetAuthToken(mnemonic, baseURLRef.current);

      if (!result.success || !result.authToken) {
        throw new Error(result.error || "Onboarding failed");
      }

      // Success! Store token (the hook's useEffect will sync onboardAuthToken)
      setAuthToken(result.authToken);
      // Store in localStorage for sharing with other pages
      localStorage.setItem("authToken", result.authToken);

      addResult(section, {
        success: true,
        message: `AUTHENTICATION SUCCESSFUL!`,
        data: {
          walletAddress,
          authToken: result.authToken.substring(0, 32) + "...",
          refreshToken: result.refreshToken,
          expiresAt: result.expiresAt,
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      const msg = error.message || String(error);
      addResult(section, {
        success: false,
        message: `Authentication failed: ${msg}`,
        error: msg,
        timestamp: Date.now(),
      });
    }
  };
  // Wallet Specific Tests (requires wallet address, session token is optional)
  const testWalletSpecific = async () => {
    const section = "Wallet Specific";

    // Recreate client with current baseURL
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });

    // Always update session token if provided
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }

    // Wait for wallet address to be available (with timeout)
    addResult(section, {
      success: true,
      message: "Waiting for wallet address to be available...",
      timestamp: Date.now(),
    });

    const maxWaitTime = 5000; // 5 seconds timeout
    const pollInterval = 200; // Check every 200ms
    let waitedTime = 0;

    // Wait for wallet address to be available
    while (!walletAddress || walletAddress.trim() === "") {
      if (waitedTime >= maxWaitTime) {
        addResult(section, {
          success: false,
          message: "Timeout: Wallet address not available after 5 seconds",
          error:
            "Wallet address is required for wallet-specific tests. Please ensure mnemonic is provided.",
          timestamp: Date.now(),
        });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      waitedTime += pollInterval;
      // Note: walletAddress is a closure variable that captures the state value
      // If the state updates during this wait, the closure won't reflect it
      // However, the wallet address is typically generated synchronously from mnemonic
      // So this wait is mainly for cases where generation is still in progress
    }

    if (!walletAddress || walletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Wallet address is required for wallet-specific tests",
        error: "Wallet address not available",
        timestamp: Date.now(),
      });
      return;
    }

    addResult(section, {
      success: true,
      message: `Wallet address available: ${walletAddress.substring(0, 10)}...`,
      timestamp: Date.now(),
    });

    try {
      // Test getBalance
      addResult(section, {
        success: true,
        message: `Testing getBalance() for asset "MORM" at address ${walletAddress.substring(0, 10)}...`,
        timestamp: Date.now(),
      });
      try {
        const balance = await infoClientRef.current.getBalance(
          walletAddress,
          "MORM",
        );
        const balanceValue = balance?.balance || "N/A";
        const balanceStr =
          typeof balanceValue === "string"
            ? balanceValue
            : JSON.stringify(balanceValue);
        addResult(section, {
          success: !!balance,
          message: `getBalance() - GET /bank/v1/balance/{address} | Asset: MORM | Balance: ${balanceStr} (raw satoshi format)`,
          data: {
            balance,
            asset: "MORM",
            walletAddress: walletAddress.substring(0, 10) + "...",
            note: "Balance is returned in satoshi format (raw integer string). Use getBalanceDecimal() for decimal format.",
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: `getBalance() failed for asset "MORM" (may require authentication)`,
          error: error.message,
          data: {
            asset: "MORM",
            walletAddress: walletAddress.substring(0, 10) + "...",
            endpoint: "GET /bank/v1/balance/{address}",
          },
          timestamp: Date.now(),
        });
      }

      // Test getBalance for USDC
      addResult(section, {
        success: true,
        message: `Testing getBalance() for asset "USDC" at address ${walletAddress.substring(0, 10)}...`,
        timestamp: Date.now(),
      });
      try {
        const balanceUSDC = await infoClientRef.current.getBalance(
          walletAddress,
          "USDC",
        );
        const balanceValueUSDC = balanceUSDC?.balance || "N/A";
        const balanceStrUSDC =
          typeof balanceValueUSDC === "string"
            ? balanceValueUSDC
            : JSON.stringify(balanceValueUSDC);
        addResult(section, {
          success: !!balanceUSDC,
          message: `getBalance() - GET /bank/v1/balance/{address} | Asset: USDC | Balance: ${balanceStrUSDC} (raw satoshi format)`,
          data: {
            balance: balanceUSDC,
            asset: "USDC",
            walletAddress: walletAddress.substring(0, 10) + "...",
            note: "Balance is returned in satoshi format (raw integer string). Use getBalanceDecimal() for decimal format.",
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: `getBalance() failed for asset "USDC" (may require authentication)`,
          error: error.message,
          data: {
            asset: "USDC",
            walletAddress: walletAddress.substring(0, 10) + "...",
            endpoint: "GET /bank/v1/balance/{address}",
          },
          timestamp: Date.now(),
        });
      }

      // Test getBalanceDecimal
      try {
        const balanceDecimal = await infoClientRef.current.getBalanceDecimal(
          walletAddress,
          "USDC",
        );
        addResult(section, {
          success: !!balanceDecimal,
          message: "getBalanceDecimal() - GET /bank/v1/balance/{address}",
          data: balanceDecimal,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBalanceDecimal() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBalances
      try {
        const balances = await infoClientRef.current.getBalances(walletAddress);
        addResult(section, {
          success: !!balances,
          message: "getBalances() - GET /bank/v1/balances/{address}",
          data: balances,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBalances() failed (may require authentication)",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getPositions
      try {
        const positions =
          await infoClientRef.current.getPositions(walletAddress);
        addResult(section, {
          success: Array.isArray(positions),
          message: `getPositions() - GET /bucket/v1/address/{address}/positions returned ${Array.isArray(positions) ? positions.length : 0} positions`,
          data: positions,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getPositions() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getOrders
      let ordersArray: any[] = [];
      try {
        const ordersResponse =
          await infoClientRef.current.getOrders(walletAddress);
        // Response structure: { orders: Order[], total_count: number, timestamp: string }
        ordersArray = ordersResponse?.orders || [];
        const isValidResponse =
          ordersResponse &&
          (Array.isArray(ordersArray) ||
            ordersResponse.total_count !== undefined);
        addResult(section, {
          success: isValidResponse,
          message: `getOrders() - GET /clob/v1/address/orders?address={address} returned ${Array.isArray(ordersArray) ? ordersArray.length : 0} orders (total_count: ${ordersResponse?.total_count ?? "N/A"})`,
          data: {
            orders: Array.isArray(ordersArray)
              ? ordersArray.slice(0, 3)
              : ordersArray,
            total_count: ordersResponse?.total_count,
            timestamp: ordersResponse?.timestamp,
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getOrders() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getOrder by ID (only if getOrders returned at least one order)
      if (Array.isArray(ordersArray) && ordersArray.length > 0) {
        const firstOrder = ordersArray[0];
        const orderId =
          firstOrder?.orderId || firstOrder?.order_id || firstOrder?.id;
        if (orderId) {
          addResult(section, {
            success: true,
            message: `Testing getOrder() for order ID: ${orderId.substring(0, 16)}... (from getOrders result)`,
            timestamp: Date.now(),
          });
          try {
            const orderById = await infoClientRef.current.getOrder(orderId);
            addResult(section, {
              success: !!orderById,
              message: `getOrder() - GET /clob/v1/order/{orderID} | Order ID: ${orderId.substring(0, 16)}...`,
              data: {
                order: orderById,
                orderId: orderId,
                source: "First order from getOrders() result",
              },
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: `getOrder() failed for order ID: ${orderId.substring(0, 16)}...`,
              error: error.message,
              data: {
                orderId: orderId,
                endpoint: "GET /clob/v1/order/{orderID}",
              },
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: false,
            message:
              "getOrder() test skipped: No valid order ID found in getOrders() result",
            error: "Order ID not found in order object",
            data: {
              order: firstOrder,
              availableFields: Object.keys(firstOrder || {}),
            },
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "getOrder() test skipped: No orders available from getOrders() (need at least 1 order)",
          data: {
            ordersCount: ordersArray.length,
            note: "getOrder() test requires at least one order from getOrders()",
          },
          timestamp: Date.now(),
        });
      }

      // Test getOrderHistory
      try {
        const orderHistoryResponse =
          await infoClientRef.current.getOrderHistory(walletAddress);
        // Handle both array response and object response with orders array
        const orderHistoryArray = Array.isArray(orderHistoryResponse)
          ? orderHistoryResponse
          : orderHistoryResponse?.orders || [];
        const isValidResponse =
          orderHistoryResponse &&
          (Array.isArray(orderHistoryArray) ||
            orderHistoryResponse.orders !== undefined);
        addResult(section, {
          success: isValidResponse,
          message: `getOrderHistory() - GET /clob/v1/address/orders/history?address={address} returned ${Array.isArray(orderHistoryArray) ? orderHistoryArray.length : 0} orders`,
          data: Array.isArray(orderHistoryArray)
            ? orderHistoryArray.slice(0, 3)
            : orderHistoryResponse,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getOrderHistory() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getTradeHistory
      try {
        const tradeHistoryResponse =
          await infoClientRef.current.getTradeHistory(walletAddress);
        // getTradeHistory always returns RawTrade[] (array)
        const tradeHistoryArray = Array.isArray(tradeHistoryResponse)
          ? tradeHistoryResponse
          : [];
        const isValidResponse = Array.isArray(tradeHistoryArray);
        addResult(section, {
          success: isValidResponse,
          message: `getTradeHistory() - GET /clob/v1/address/trades?address={address} returned ${tradeHistoryArray.length} trades`,
          data: tradeHistoryArray.slice(0, 3),
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getTradeHistory() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBuckets (direct SDK call)
      try {
        const buckets = await infoClientRef.current.getBuckets(walletAddress);
        addResult(section, {
          success: Array.isArray(buckets) || buckets !== undefined,
          message: `getBuckets() - GET /bucket/v1/address/{address}/buckets returned ${Array.isArray(buckets) ? buckets.length : "data"}`,
          data: Array.isArray(buckets) ? buckets.slice(0, 3) : buckets,
          timestamp: Date.now(),
        });

        // If buckets array exists and has items, test getBucket with first bucket ID
        if (Array.isArray(buckets) && buckets.length > 0) {
          const firstBucket = buckets[0];
          const bucketId = firstBucket.bucket_id;
          if (bucketId) {
            try {
              const bucket = await infoClientRef.current.getBucket(bucketId);
              addResult(section, {
                success: !!bucket,
                message: `getBucket() - GET /bucket/v1/bucket/{bucket_id} (${bucketId.substring(0, 16)}...)`,
                data: bucket,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `getBucket(${bucketId.substring(0, 16)}...) failed`,
                error: error.message,
                timestamp: Date.now(),
              });
            }
          }
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBuckets() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test useBuckets hook
      addResult(section, {
        success: true,
        message: "Testing useBuckets hook...",
        timestamp: Date.now(),
      });
      try {
        // Refresh buckets using the hook
        await refreshBuckets();
        addResult(section, {
          success: !bucketsError && !bucketsLoading,
          message: `useBuckets hook: ${hookBuckets.length} buckets loaded`,
          data: {
            buckets: hookBuckets.slice(0, 3),
            loading: bucketsLoading,
            error: bucketsError,
            totalBuckets: hookBuckets.length,
          },
          timestamp: Date.now(),
        });

        // If hook has buckets, test getBucket with first one
        if (hookBuckets.length > 0) {
          const firstHookBucket = hookBuckets[0];
          const hookBucketId = firstHookBucket.bucketId?.toString();
          if (hookBucketId) {
            try {
              const bucket =
                await infoClientRef.current.getBucket(hookBucketId);
              addResult(section, {
                success: !!bucket,
                message: `getBucket() - GET /bucket/v1/bucket/{bucket_id} via hook bucket ID (${hookBucketId.substring(0, 16)}...)`,
                data: bucket,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `getBucket() via hook bucket ID failed`,
                error: error.message,
                timestamp: Date.now(),
              });
            }
          }
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "useBuckets hook test failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Wallet Specific test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  const testInfoClient = async () => {
    const section = "InfoClient";

    // Recreate client with current baseURL
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });

    // Always update session token if provided
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }

    try {
      // Test getMarkets - use hook to retain data
      addResult(section, {
        success: true,
        message: "Testing getMarkets()...",
        timestamp: Date.now(),
      });

      // Fetch markets using the hook to retain data in store
      const marketsData = await fetchMarkets();
      // Store in ref immediately so it's available to other test functions
      fetchedMarketsRef.current = Array.isArray(marketsData) ? marketsData : [];
      const marketCount = Array.isArray(marketsData) ? marketsData.length : 0;

      // Find first active market, or first market in list
      const firstMarket =
        marketCount > 0
          ? (marketsData.find((m: any) => m.active || m.status === "active") ??
            marketsData[0])
          : null;

      // Use exact market_id (string) from API, fallback to id converted to string
      const firstMarketId =
        firstMarket?.market_id ?? firstMarket?.id?.toString() ?? null;

      addResult(section, {
        success: Array.isArray(marketsData) && marketCount > 0,
        message: `getMarkets() - GET /market/v1/all returned ${marketCount} markets${firstMarketId ? ` | Using market ID: ${firstMarketId} (${firstMarket?.moniker || firstMarket?.symbol || "auto-selected"})` : ""}`,
        data: {
          count: marketCount,
          defaultMarketId: firstMarketId,
          defaultMarket: firstMarket
            ? {
                id: firstMarket.id,
                market_id: firstMarket.market_id,
                moniker: firstMarket.moniker,
                symbol: firstMarket.symbol,
                active: firstMarket.active,
                status: firstMarket.status,
              }
            : null,
          markets: marketsData.slice(0, 5), // Show first 5
        },
        timestamp: Date.now(),
      });

      // Automatically set the first market ID for demo purposes (use exact string)
      if (firstMarketId) {
        setMarketId(firstMarketId);
        // Store in ref immediately so it's available to other test functions
        fetchedMarketIdRef.current = firstMarketId;

        const marketDisplayName =
          firstMarket?.moniker ||
          firstMarket?.symbol ||
          (firstMarket?.baseAsset && firstMarket?.quoteAsset
            ? `${firstMarket.baseAsset}-${firstMarket.quoteAsset}`
            : "Unknown");

        addResult(section, {
          success: true,
          message: `Markets loaded: ${marketCount} | Auto-selected: ${marketDisplayName} (ID: ${firstMarketId.substring(0, 10)}...)`,
          data: {
            selectedMarketId: firstMarketId,
            count: marketCount,
            market: firstMarket,
          },
          timestamp: Date.now(),
        });

        // Critical: Wait for React state to propagate before next tests
        addResult(section, {
          success: true,
          message: `Waiting 1.5s for market selection to take effect...`,
          timestamp: Date.now(),
        });

        await new Promise((resolve) => setTimeout(resolve, 1500)); // 1.5 second delay

        addResult(section, {
          success: true,
          message: `Market ID ready: ${firstMarketId}`,
          data: { marketId: firstMarketId },
          timestamp: Date.now(),
        });

        // Optional: Confirm in UI
        addResult(section, {
          success: true,
          message: `Proceeding with market: ${marketDisplayName}`,
          timestamp: Date.now(),
        });
      }

      // Test market-specific endpoints (only if market ID is available)
      // After the delay, React state should be updated, but use the ID we just set to be safe
      const currentMarketId = firstMarketId || effectiveMarketId || marketId;
      if (currentMarketId) {
        // Test getOrderbook (with decimal conversion)
        addResult(section, {
          success: true,
          message: `Testing getOrderbook(${currentMarketId}, true)...`,
          timestamp: Date.now(),
        });
        try {
          const orderbook = await infoClientRef.current.getOrderbook(
            currentMarketId,
            true,
          );
          addResult(section, {
            success: !!orderbook && (!!orderbook.bids || !!orderbook.asks),
            message:
              "getOrderbook() - GET /clob/v1/orderbook/{marketId} with decimal conversion",
            data: {
              bids: orderbook?.bids?.slice(0, 3),
              asks: orderbook?.asks?.slice(0, 3),
            },
            timestamp: Date.now(),
          });

          // Test getOrderbook (raw satoshi)
          const orderbookRaw = await infoClientRef.current.getOrderbook(
            currentMarketId,
            false,
          );
          addResult(section, {
            success: !!orderbookRaw && (!!orderbookRaw.bids || !!orderbookRaw.asks),
            message:
              "getOrderbook() - GET /clob/v1/orderbook/{marketId} raw satoshi format",
            data: {
              bids: orderbookRaw?.bids?.slice(0, 2),
              asks: orderbookRaw?.asks?.slice(0, 2),
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getOrderbook() - GET /clob/v1/orderbook/{marketId} failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Test getTicker
        addResult(section, {
          success: true,
          message: `Testing getTicker(${currentMarketId})...`,
          timestamp: Date.now(),
        });
        try {
          const ticker = await infoClientRef.current.getTicker(currentMarketId);
          addResult(section, {
            success: !!ticker,
            message: "getTicker() - GET /market/v1/market/{marketId}/stats",
            data: ticker,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getTicker() - GET /market/v1/market/{marketId}/stats failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Test getRecentTrades
        addResult(section, {
          success: true,
          message: `Testing getRecentTrades(${currentMarketId}, 10)...`,
          timestamp: Date.now(),
        });
        try {
          const trades = await infoClientRef.current.getRecentTrades(
            currentMarketId,
            10,
          );
          addResult(section, {
            success: Array.isArray(trades),
            message: `getRecentTrades() - GET /clob/v1/trades?market_id={marketId} returned ${Array.isArray(trades) ? trades.length : 0} trades`,
            data: trades?.slice(0, 3),
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getRecentTrades() - GET /clob/v1/trades?market_id={marketId} failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: false,
          message: "Skipping market-specific tests: No market ID available",
          error: "Please ensure markets are loaded and a market is selected",
          timestamp: Date.now(),
        });
      }

      // Test router/v1 endpoints (public, no auth required)
      addResult(section, {
        success: true,
        message: "Testing router/v1 endpoints...",
        timestamp: Date.now(),
      });

      // Test getSupportedActions
      try {
        const supportedActions =
          await infoClientRef.current.getSupportedActions();
        addResult(section, {
          success:
            Array.isArray(supportedActions?.actions) ||
            supportedActions?.actions !== undefined,
          message:
            "getSupportedActions() - GET /router/v1/tx/supported-actions",
          data: supportedActions,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "getSupportedActions() - GET /router/v1/tx/supported-actions failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test validateAction
      try {
        const validateResult =
          await infoClientRef.current.validateAction("clob::submit_order");
        addResult(section, {
          success:
            validateResult?.valid !== undefined || validateResult !== undefined,
          message:
            'validateAction() - GET /router/v1/tx/validate-action ("clob::submit_order")',
          data: validateResult,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "validateAction() - GET /router/v1/tx/validate-action failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthSummary
      try {
        const healthSummary = await infoClientRef.current.getHealthSummary();
        addResult(section, {
          success: !!healthSummary,
          message: "getHealthSummary() - GET /router/v1/health/summary",
          data: healthSummary,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthSummary() - GET /router/v1/health/summary failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthDaemons
      try {
        const healthDaemons = await infoClientRef.current.getHealthDaemons();
        addResult(section, {
          success: !!healthDaemons,
          message: "getHealthDaemons() - GET /router/v1/health/daemons",
          data: healthDaemons,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthDaemons() - GET /router/v1/health/daemons failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getServerTime
      try {
        const serverTime = await infoClientRef.current.getServerTime();
        addResult(section, {
          success:
            !!serverTime &&
            (serverTime.serverTime !== undefined || serverTime !== undefined),
          message: "getServerTime() - GET /router/v1/time",
          data: serverTime,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getServerTime() - GET /router/v1/time failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getMetricsTPS
      try {
        const metricsTPS = await infoClientRef.current.getMetricsTPS();
        addResult(section, {
          success: !!metricsTPS,
          message: "getMetricsTPS() - GET /router/v1/metrics/tps",
          data: metricsTPS,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getMetricsTPS() - GET /router/v1/metrics/tps failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getMetricsLatency
      try {
        const metricsLatency = await infoClientRef.current.getMetricsLatency();
        addResult(section, {
          success: !!metricsLatency,
          message: "getMetricsLatency() - GET /router/v1/metrics/latency",
          data: metricsLatency,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "getMetricsLatency() - GET /router/v1/metrics/latency failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "InfoClient test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // ExchangeClient Tests
  const testExchangeClient = async () => {
    const section = "ExchangeClient";
    if (!mnemonic) {
      addResult(section, {
        success: false,
        message: "Mnemonic required for ExchangeClient tests",
        error: "No mnemonic provided",
        timestamp: Date.now(),
      });
      return;
    }

    // Read market ID from page state (UI input) or from loaded markets
    // Try in order: 1) marketId from state, 2) effectiveMarketId (has fallback), 3) from ref (immediately available), 4) from markets array
    let currentMarketId =
      marketId || effectiveMarketId || fetchedMarketIdRef.current || "";

    // If still empty, try to get from fetched markets ref (loaded in previous sections, immediately available)
    if (!currentMarketId || currentMarketId.trim() === "") {
      if (fetchedMarketsRef.current && fetchedMarketsRef.current.length > 0) {
        // Find first active market, or first market in list (same logic as testInfoClient)
        const firstMarket =
          fetchedMarketsRef.current.find(
            (m: any) => m.active || m.status === "active",
          ) ?? fetchedMarketsRef.current[0];
        if (firstMarket) {
          currentMarketId =
            firstMarket.market_id ?? firstMarket.id?.toString() ?? "";
          fetchedMarketIdRef.current = currentMarketId; // Store for next time
          addResult(section, {
            success: true,
            message: `Market ID not in state, using from fetched markets ref: ${currentMarketId.substring(0, 16)}...`,
            data: {
              source: "fetchedMarketsRef",
              marketId: currentMarketId,
              market: {
                moniker: firstMarket.moniker,
                symbol: firstMarket.symbol,
                active: firstMarket.active,
              },
            },
            timestamp: Date.now(),
          });
        }
      } else if (markets && markets.length > 0) {
        // Fallback to markets state (might be updated by now)
        const firstMarket =
          markets.find((m: any) => m.active || m.status === "active") ??
          markets[0];
        if (firstMarket) {
          currentMarketId =
            firstMarket.market_id ?? firstMarket.id?.toString() ?? "";
          addResult(section, {
            success: true,
            message: `Market ID not in state, using from markets state: ${currentMarketId.substring(0, 16)}...`,
            data: {
              source: "markets state",
              marketId: currentMarketId,
              market: {
                moniker: firstMarket.moniker,
                symbol: firstMarket.symbol,
                active: firstMarket.active,
              },
            },
            timestamp: Date.now(),
          });
        }
      }
    }

    // Final check - if still empty, show error with debug info
    if (!currentMarketId || currentMarketId.trim() === "") {
      addResult(section, {
        success: false,
        message: "Market ID required from page or loaded markets",
        error: `Please enter or select a market ID in the configuration section, or ensure markets are loaded first. Debug: marketId="${marketId || "(empty)"}", effectiveMarketId="${effectiveMarketId || "(empty)"}", fetchedMarketIdRef="${fetchedMarketIdRef.current || "(empty)"}", fetchedMarketsRef.length=${fetchedMarketsRef.current.length}, markets.length=${markets.length}`,
        data: {
          marketId: marketId || "(empty)",
          effectiveMarketId: effectiveMarketId || "(empty)",
          defaultMarketId: defaultMarketId || "(empty)",
          fetchedMarketIdRef: fetchedMarketIdRef.current || "(empty)",
          fetchedMarketsRefCount: fetchedMarketsRef.current.length,
          marketsCount: markets.length,
          marketsAvailable:
            markets.length > 0 || fetchedMarketsRef.current.length > 0,
        },
        timestamp: Date.now(),
      });
      return;
    }

    // Read wallet address from page state (UI input)
    const currentWalletAddress = walletAddress || "";
    if (!currentWalletAddress || currentWalletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Wallet address required from page",
        error:
          "Wallet address should be generated from mnemonic or entered manually. Please ensure mnemonic is valid.",
        timestamp: Date.now(),
      });
      return;
    }

    // Determine source of market ID for logging
    const marketIdSource = marketId
      ? "page state (marketId)"
      : effectiveMarketId
        ? "page state (effectiveMarketId)"
        : "loaded markets array";

    addResult(section, {
      success: true,
      message: `Using market ID from ${marketIdSource}: ${currentMarketId.substring(0, 16)}...`,
      data: {
        marketId: currentMarketId,
        source: marketIdSource,
      },
      timestamp: Date.now(),
    });

    addResult(section, {
      success: true,
      message: `Using wallet address from page: ${currentWalletAddress.substring(0, 10)}...`,
      data: { walletAddress: currentWalletAddress },
      timestamp: Date.now(),
    });

    // Log signing tokens if available
    if (sessionToken || authToken) {
      addResult(section, {
        success: true,
        message: `Using signing tokens: ${sessionToken ? "Session Token" : ""} ${authToken ? "Auth Token" : ""}`,
        data: {
          hasSessionToken: !!sessionToken,
          hasAuthToken: !!authToken,
        },
        timestamp: Date.now(),
      });
    }

    // Recreate client with current config
    exchangeClientRef.current = new ExchangeClient({
      baseURL: baseURLRef.current,
      mnemonic: mnemonicRef.current,
    });

    try {
      // Initialize
      addResult(section, {
        success: true,
        message: "Initializing ExchangeClient...",
        timestamp: Date.now(),
      });
      await exchangeClientRef.current.initialize();
      const walletAddr = exchangeClientRef.current.getWalletAddress();

      // Verify wallet address matches the one from page
      if (walletAddr.toLowerCase() !== currentWalletAddress.toLowerCase()) {
        addResult(section, {
          success: false,
          message: "Wallet address mismatch",
          error: `ExchangeClient wallet (${walletAddr}) does not match page wallet (${currentWalletAddress})`,
          timestamp: Date.now(),
        });
        return;
      }

      addResult(section, {
        success: true,
        message: `ExchangeClient initialized successfully (Wallet: ${walletAddr.substring(0, 10)}...)`,
        data: {
          walletAddress: walletAddr,
          marketId: currentMarketId,
          verified: true,
        },
        timestamp: Date.now(),
      });

      // Use marketId as string directly (no conversion needed)
      const marketIdString = currentMarketId;
      addResult(section, {
        success: true,
        message: `Using market ID as string: ${marketIdString}`,
        data: { marketId: marketIdString },
        timestamp: Date.now(),
      });

      // Store orderId from placeOrder for cancelOrder test
      let placedOrderId: string | null = null;

      // Test placeOrder with validation for btc-usdc-perp market
      addResult(section, {
        success: true,
        message:
          "Testing placeOrder with validation for btc-usdc-perp market...",
        timestamp: Date.now(),
      });

      // Fetch buckets if not already available
      let availableBucketsForOrderTest: any[] = [];
      try {
        if (!infoClientRef.current) {
          infoClientRef.current = new InfoClient({
            baseURL: baseURLRef.current,
          });
        }
        if (sessionToken && infoClientRef.current) {
          infoClientRef.current.setSessionToken(sessionToken);
        }

        const buckets = await infoClientRef.current.getBuckets(walletAddr);
        availableBucketsForOrderTest = Array.isArray(buckets) ? buckets : [];

        addResult(section, {
          success: true,
          message: `Fetched ${availableBucketsForOrderTest.length} bucket(s) for order test`,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "Failed to fetch buckets for order test",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Find btc-usdc-perp market
      let btcUsdcPerpMarketId: string | null = null;
      try {
        const allMarkets =
          fetchedMarketsRef.current.length > 0
            ? fetchedMarketsRef.current
            : markets;

        const btcUsdcPerpMarket = allMarkets.find((m: any) => {
          const moniker = (m.moniker || "").toLowerCase();
          const symbol = (m.symbol || "").toLowerCase();
          return (
            moniker.includes("btc-usdc-perp") ||
            moniker.includes("btcusdcperp") ||
            symbol.includes("btc-usdc-perp") ||
            symbol.includes("btcusdcperp") ||
            (m.baseAsset &&
              m.quoteAsset &&
              m.baseAsset.toLowerCase() === "btc" &&
              m.quoteAsset.toLowerCase() === "usdc")
          );
        });

        if (btcUsdcPerpMarket) {
          btcUsdcPerpMarketId =
            btcUsdcPerpMarket.market_id ||
            btcUsdcPerpMarket.id?.toString() ||
            null;
          addResult(section, {
            success: !!btcUsdcPerpMarketId,
            message: `Found btc-usdc-perp market: ${btcUsdcPerpMarketId?.substring(0, 16)}...`,
            data: {
              marketId: btcUsdcPerpMarketId,
              moniker: btcUsdcPerpMarket.moniker,
              symbol: btcUsdcPerpMarket.symbol,
            },
            timestamp: Date.now(),
          });
        } else {
          addResult(section, {
            success: false,
            message: "btc-usdc-perp market not found in available markets",
            error: "Please ensure btc-usdc-perp market is available",
            timestamp: Date.now(),
          });
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "Failed to find btc-usdc-perp market",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // If market found, proceed with comprehensive order placement test
      if (btcUsdcPerpMarketId) {
        try {
          // Get default bucket for balance checks
          let defaultBucketId: string | null = null;
          if (availableBucketsForOrderTest.length > 0) {
            const defaultBucket = availableBucketsForOrderTest[0];
            defaultBucketId =
              defaultBucket.bucket_id ||
              defaultBucket.id ||
              defaultBucket.bucketId ||
              null;
          }

          if (!defaultBucketId) {
            addResult(section, {
              success: false,
              message: "Default bucket not available for balance validation",
              error: "No buckets found",
              timestamp: Date.now(),
            });
          } else {
            // Step 1: Load default bucket balance and validate sufficient margin
            addResult(section, {
              success: true,
              message:
                "Step 1: Loading default bucket balance and checking available margin...",
              timestamp: Date.now(),
            });

            // Order parameters
            const orderQuantity = 1.01; // 1.01 BTC (quantity in base asset)
            const orderLeverage = 100.0; // 100x leverage
            const orderPrice = 10500.0; // 10500 USDC per BTC (price in quote asset)
            const leverageStr = toSatoshi(orderLeverage.toString());

            // Calculate required margin: (quantity * price) / leverage
            // For BTC-USDC-PERP: (1.01 BTC * 10500 USDC/BTC) / 100 = 106.05 USDC
            const requiredMargin = (orderQuantity * orderPrice) / orderLeverage;

            let bucketBalanceBefore: number = 0;
            let depositedMargin: number = 0;
            let availableMargin: number = 0;
            let totalEquity: number = 0;

            try {
              if (!infoClientRef.current) {
                throw new Error("InfoClient not initialized");
              }
              const bucketResponse =
                await infoClientRef.current.getBucket(defaultBucketId);

              // Extract all margin-related values
              availableMargin = fromSatoshi(
                bucketResponse.available_margin || "0",
              );
              depositedMargin = fromSatoshi(
                bucketResponse.deposited_margin || "0",
              );
              totalEquity = fromSatoshi(bucketResponse.total_equity || "0");
              // available_margin is what can be used for new positions
              bucketBalanceBefore = availableMargin; // Keep for reference in Step 1 validation

              addResult(section, {
                success: true,
                message: `Step 1: Default bucket balance loaded`,
                data: {
                  availableMargin: availableMargin.toFixed(2),
                  depositedMargin: depositedMargin.toFixed(2),
                  totalEquity: totalEquity.toFixed(2),
                  requiredMargin: requiredMargin.toFixed(2),
                  raw: bucketResponse,
                },
                timestamp: Date.now(),
              });

              // Validate sufficient available margin
              addResult(section, {
                success: true,
                message: `Step 1: Validating sufficient available margin (required: ${requiredMargin.toFixed(2)} USDC, available: ${availableMargin.toFixed(2)} USDC)...`,
                timestamp: Date.now(),
              });

              const hasSufficientMargin = availableMargin >= requiredMargin;

              addResult(section, {
                success: hasSufficientMargin,
                message: `Step 1: Margin validation ${hasSufficientMargin ? "PASSED" : "FAILED"}`,
                data: {
                  availableMargin: availableMargin.toFixed(2),
                  requiredMargin: requiredMargin.toFixed(2),
                  marginDifference: (availableMargin - requiredMargin).toFixed(
                    2,
                  ),
                  hasSufficientMargin,
                  orderQuantity: `${orderQuantity.toFixed(2)} BTC`,
                  orderPrice: `${orderPrice.toFixed(2)} USDC/BTC`,
                  orderLeverage: orderLeverage.toFixed(2),
                  calculation: `(${orderQuantity.toFixed(2)} BTC * ${orderPrice.toFixed(2)} USDC/BTC) / ${orderLeverage.toFixed(2)} = ${requiredMargin.toFixed(2)} USDC`,
                },
                timestamp: Date.now(),
              });

              if (!hasSufficientMargin) {
                addResult(section, {
                  success: false,
                  message: `Step 1: Insufficient available margin. Required: ${requiredMargin.toFixed(2)} USDC, Available: ${availableMargin.toFixed(2)} USDC`,
                  error: `Available margin (${availableMargin.toFixed(2)} USDC) is less than required margin (${requiredMargin.toFixed(2)} USDC)`,
                  data: {
                    availableMargin: availableMargin.toFixed(2),
                    requiredMargin: requiredMargin.toFixed(2),
                    shortfall: (requiredMargin - availableMargin).toFixed(2),
                  },
                  timestamp: Date.now(),
                });
                throw new Error(
                  `Insufficient available margin: ${availableMargin.toFixed(2)} USDC available, ${requiredMargin.toFixed(2)} USDC required`,
                );
              }

              addResult(section, {
                success: true,
                message: `Step 1: Sufficient margin available. Proceeding to place order...`,
                data: {
                  availableMargin: availableMargin.toFixed(2),
                  requiredMargin: requiredMargin.toFixed(2),
                  marginRemaining: (availableMargin - requiredMargin).toFixed(
                    2,
                  ),
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 1: Failed to load or validate bucket balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 2: Place order (10 USDC, 100x leverage, price 10500)
            addResult(section, {
              success: true,
              message: `Step 2: Preparing to place order - Quantity: ${orderQuantity.toFixed(2)} BTC, Leverage: ${orderLeverage}x, Price: ${orderPrice.toFixed(2)} USDC/BTC...`,
              timestamp: Date.now(),
            });

            // Step 2.1: Check current default bucket has enough balance (available_margin)
            addResult(section, {
              success: true,
              message:
                "Step 2.1: Re-checking default bucket available margin before placing order...",
              timestamp: Date.now(),
            });

            let currentAvailableMargin: number = 0;
            try {
              if (!infoClientRef.current) {
                throw new Error("InfoClient not initialized");
              }
              const currentBucketResponse =
                await infoClientRef.current.getBucket(defaultBucketId);

              currentAvailableMargin = fromSatoshi(
                currentBucketResponse.available_margin || "0",
              );

              addResult(section, {
                success: true,
                message: `Step 2.1: Current available margin: ${currentAvailableMargin.toFixed(2)} USDC`,
                data: {
                  currentAvailableMargin: currentAvailableMargin.toFixed(2),
                  requiredMargin: requiredMargin.toFixed(2),
                  previousAvailableMargin: availableMargin.toFixed(2),
                  raw: currentBucketResponse,
                },
                timestamp: Date.now(),
              });

              // Validate sufficient available margin again
              const stillHasSufficientMargin =
                currentAvailableMargin >= requiredMargin;

              addResult(section, {
                success: stillHasSufficientMargin,
                message: `Step 2.1: Margin check ${stillHasSufficientMargin ? "PASSED" : "FAILED"}`,
                data: {
                  currentAvailableMargin: currentAvailableMargin.toFixed(2),
                  requiredMargin: requiredMargin.toFixed(2),
                  marginDifference: (
                    currentAvailableMargin - requiredMargin
                  ).toFixed(2),
                  stillHasSufficientMargin,
                },
                timestamp: Date.now(),
              });

              if (!stillHasSufficientMargin) {
                addResult(section, {
                  success: false,
                  message: `Step 2.1: Insufficient available margin. Required: ${requiredMargin.toFixed(2)} USDC, Available: ${currentAvailableMargin.toFixed(2)} USDC`,
                  error: `Available margin (${currentAvailableMargin.toFixed(2)} USDC) is less than required margin (${requiredMargin.toFixed(2)} USDC)`,
                  data: {
                    currentAvailableMargin: currentAvailableMargin.toFixed(2),
                    requiredMargin: requiredMargin.toFixed(2),
                    shortfall: (
                      requiredMargin - currentAvailableMargin
                    ).toFixed(2),
                  },
                  timestamp: Date.now(),
                });
                throw new Error(
                  `Insufficient available margin: ${currentAvailableMargin.toFixed(2)} USDC available, ${requiredMargin.toFixed(2)} USDC required`,
                );
              }

              addResult(section, {
                success: true,
                message: `Step 2.1: Sufficient margin confirmed. Proceeding with order placement...`,
                data: {
                  currentAvailableMargin: currentAvailableMargin.toFixed(2),
                  requiredMargin: requiredMargin.toFixed(2),
                  marginRemaining: (
                    currentAvailableMargin - requiredMargin
                  ).toFixed(2),
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message:
                  "Step 2.1: Failed to validate available margin before order placement",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 2.2: Place order
            addResult(section, {
              success: true,
              message: `Step 2.2: Placing order - Quantity: ${orderQuantity.toFixed(2)} BTC, Leverage: ${orderLeverage}x, Price: ${orderPrice.toFixed(2)} USDC/BTC...`,
              timestamp: Date.now(),
            });

            // Get bucketId from first available bucket
            if (!hookBuckets || hookBuckets.length === 0) {
              throw new Error("No buckets available. Please create a bucket first.");
            }
            const firstBucket = hookBuckets[0];
            const bucketId = firstBucket.bucketId;
            if (!bucketId) {
              throw new Error("Bucket ID not found in bucket data");
            }

            let orderResult: any = null;
            let orderId: string | null = null;
            const orderRequest = {
              marketId: btcUsdcPerpMarketId,
              bucketId: bucketId,
              side: "buy" as const,
              orderType: "LIMIT" as const,
              price: orderPrice,
              quantity: orderQuantity,
              timeInForce: "GTC" as const,
              leverage: leverageStr,
            };

            // Retry config for nonce errors (10 retries)
            const retryConfig: RetryConfig = {
              maxRetries: 10,
              baseDelay: 500,
              maxDelay: 2000,
              jitter: true,
              multiplier: 1.5,
            };

            let attemptCount = 0;

            try {
              orderResult = await executeWithRetry(
                retryConfig,
                async () => {
                  attemptCount++;
                  if (attemptCount > 1) {
                    addResult(section, {
                      success: true,
                      message: `Step 2.2: Retrying order placement (attempt ${attemptCount}/${retryConfig.maxRetries + 1})...`,
                      data: {
                        attemptCount,
                      },
                      timestamp: Date.now(),
                    });
                  }

                  if (!exchangeClientRef.current) {
                    throw new Error("ExchangeClient not initialized");
                  }
                  const result =
                    await exchangeClientRef.current.placeOrder(orderRequest);
                  const resultOrderId = result?.order_id || null;

                  if (!result || !resultOrderId) {
                    throw new Error(
                      "Order placed but order_id not found in response",
                    );
                  }

                  return result;
                },
                async (error: any) => {
                  // Check if this is a nonce error
                  const isNonceError = shouldRefreshNonce(error);

                  if (isNonceError) {
                    addResult(section, {
                      success: false,
                      message: `Step 2.2: Nonce error detected (attempt ${attemptCount}/${retryConfig.maxRetries + 1}): ${error?.message || String(error)}`,
                      data: {
                        error: error?.message || String(error),
                        errorType: "nonce error",
                        attemptCount,
                        willRetry: attemptCount <= retryConfig.maxRetries,
                      },
                      timestamp: Date.now(),
                    });

                    // Refresh nonce from server before retry
                    try {
                      if (!exchangeClientRef.current) {
                        throw new Error("ExchangeClient not initialized");
                      }
                      await exchangeClientRef.current.refreshNonceFromServer();
                      addResult(section, {
                        success: true,
                        message:
                          "Step 2.2: Refreshed nonce from server, will retry...",
                        timestamp: Date.now(),
                      });
                    } catch (refreshError: any) {
                      addResult(section, {
                        success: false,
                        message:
                          "Step 2.2: Failed to refresh nonce from server",
                        error: refreshError?.message || String(refreshError),
                        timestamp: Date.now(),
                      });
                      // Still retry, as getNextNonce will handle it
                    }

                    return true; // Retry on nonce errors
                  }

                  // Not a nonce error, don't retry
                  addResult(section, {
                    success: false,
                    message: `Step 2.2: Failed to place order (non-retryable error)`,
                    error: error?.message || String(error),
                    data: {
                      error: error?.message || String(error),
                      isNonceError: false,
                    },
                    timestamp: Date.now(),
                  });
                  return false; // Don't retry non-nonce errors
                },
              );

              orderId = orderResult?.order_id || null;

              addResult(section, {
                success: !!orderResult && !!orderId,
                message: `Step 2.2: Order placed successfully${attemptCount > 1 ? ` (after ${attemptCount - 1} retry${attemptCount > 2 ? "ies" : ""})` : ""}. Order ID: ${orderId?.substring(0, 16)}...`,
                data: {
                  orderResult,
                  orderId,
                  requestBody: orderRequest,
                  attemptCount,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `Step 2.2: Failed to place order after ${attemptCount} attempt${attemptCount > 1 ? "s" : ""}`,
                error: error?.message || String(error),
                data: {
                  error: error?.message || String(error),
                  attemptCount,
                  maxRetriesReached: attemptCount > retryConfig.maxRetries,
                },
                timestamp: Date.now(),
              });
              throw error;
            }

            // If we exhausted all retries without success
            if (!orderResult || !orderId) {
              throw new Error(
                `Failed to place order after ${attemptCount} attempts`,
              );
            }

            // Wait a bit for the order to be processed
            await new Promise((resolve) => setTimeout(resolve, 1500));

            // Step 3: Validate pending order details
            if (orderId) {
              addResult(section, {
                success: true,
                message: "Step 3: Validating pending order details...",
                timestamp: Date.now(),
              });

              try {
                if (!infoClientRef.current) {
                  throw new Error("InfoClient not initialized");
                }
                const pendingOrder =
                  await infoClientRef.current.getOrder(orderId);

                if (pendingOrder) {
                  const orderPriceFromApi = fromSatoshi(
                    pendingOrder.price || "0",
                  );
                  const orderQuantityFromApi = fromSatoshi(
                    pendingOrder.quantity || "0",
                  );
                  const orderLeverageFromApi = fromSatoshi(
                    pendingOrder.leverage || "0",
                  );
                  const orderSide = pendingOrder.side || "";
                  const orderMarketId = pendingOrder.market_id || "";

                  const priceValid =
                    Math.abs(orderPriceFromApi - orderPrice) < 0.01;
                  const quantityValid =
                    Math.abs(orderQuantityFromApi - orderQuantity) < 0.01;
                  const leverageValid =
                    Math.abs(orderLeverageFromApi - orderLeverage) < 0.01;
                  const marketIdValid = orderMarketId === btcUsdcPerpMarketId;
                  const sideValid =
                    orderSide.toUpperCase().includes("BUY");

                  addResult(section, {
                    success:
                      priceValid &&
                      quantityValid &&
                      leverageValid &&
                      marketIdValid &&
                      sideValid,
                    message: `Step 3: Order validation ${priceValid && quantityValid && leverageValid && marketIdValid && sideValid ? "PASSED" : "FAILED"}`,
                    data: {
                      orderId,
                      expectedPrice: orderPrice.toFixed(2),
                      actualPrice: orderPriceFromApi.toFixed(2),
                      priceValid,
                      expectedQuantity: `${orderQuantity.toFixed(2)} BTC`,
                      actualQuantity: `${orderQuantityFromApi.toFixed(2)} BTC`,
                      quantityValid,
                      expectedLeverage: orderLeverage.toFixed(2),
                      actualLeverage: orderLeverageFromApi.toFixed(2),
                      leverageValid,
                      expectedMarketId: btcUsdcPerpMarketId,
                      actualMarketId: orderMarketId,
                      marketIdValid,
                      expectedSide: "buy",
                      actualSide: orderSide,
                      sideValid,
                      orderStatus: pendingOrder.status,
                      rawOrder: pendingOrder,
                    },
                    timestamp: Date.now(),
                  });

                  if (!priceValid) {
                    addResult(section, {
                      success: false,
                      message: `Order price mismatch: Expected ${orderPrice.toFixed(2)}, got ${orderPriceFromApi.toFixed(2)}`,
                      timestamp: Date.now(),
                    });
                  }
                  if (!quantityValid) {
                    addResult(section, {
                      success: false,
                      message: `Order quantity mismatch: Expected ${orderQuantity.toFixed(2)} BTC, got ${orderQuantityFromApi.toFixed(2)} BTC`,
                      timestamp: Date.now(),
                    });
                  }
                  if (!leverageValid) {
                    addResult(section, {
                      success: false,
                      message: `Order leverage mismatch: Expected ${orderLeverage.toFixed(2)}, got ${orderLeverageFromApi.toFixed(2)}`,
                      timestamp: Date.now(),
                    });
                  }
                  if (!marketIdValid) {
                    addResult(section, {
                      success: false,
                      message: `Order market ID mismatch: Expected ${btcUsdcPerpMarketId}, got ${orderMarketId}`,
                      timestamp: Date.now(),
                    });
                  }
                  if (!sideValid) {
                    addResult(section, {
                      success: false,
                      message: `Order side mismatch: Expected buy, got ${orderSide}`,
                      timestamp: Date.now(),
                    });
                  }
                } else {
                  addResult(section, {
                    success: false,
                    message: "Step 3: Failed to retrieve pending order",
                    error: "Order not found",
                    timestamp: Date.now(),
                  });
                }
              } catch (error: any) {
                addResult(section, {
                  success: false,
                  message: "Step 3: Failed to validate pending order",
                  error: error.message,
                  timestamp: Date.now(),
                });
              }
            }

            // Step 4: Load default bucket balance after placing order
            addResult(section, {
              success: true,
              message:
                "Step 4: Loading default bucket USDC balance after placing order...",
              timestamp: Date.now(),
            });

            let bucketBalanceAfter: number = 0;
            try {
              if (!infoClientRef.current) {
                throw new Error("InfoClient not initialized");
              }
              const bucketResponseAfter =
                await infoClientRef.current.getBucket(defaultBucketId);
              bucketBalanceAfter = fromSatoshi(
                bucketResponseAfter.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 4: Default bucket USDC balance after: ${bucketBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: bucketBalanceAfter,
                  raw: bucketResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 4: Failed to load bucket balance after order",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 5: Validate bucket balance change
            addResult(section, {
              success: true,
              message: "Step 5: Validating bucket balance change...",
              timestamp: Date.now(),
            });

            // Use the current available margin from Step 2.1 as the baseline (most recent before order)
            const bucketBalanceChange =
              currentAvailableMargin - bucketBalanceAfter;
            // For a limit order, margin should be reserved (bucket balance should decrease)
            // The margin reserved = quantity * price / leverage
            const expectedMarginReserved = requiredMargin;
            const marginReservedValid =
              Math.abs(bucketBalanceChange - expectedMarginReserved) < 0.01;

            addResult(section, {
              success: marginReservedValid,
              message: `Step 5: Bucket balance validation ${marginReservedValid ? "PASSED" : "FAILED"}`,
              data: {
                availableMarginBeforeOrder: currentAvailableMargin.toFixed(2),
                availableMarginAfterOrder: bucketBalanceAfter.toFixed(2),
                marginReserved: bucketBalanceChange.toFixed(2),
                expectedMarginReserved: expectedMarginReserved.toFixed(2),
                marginReservedValid,
                orderQuantity: `${orderQuantity.toFixed(2)} BTC`,
                orderPrice: `${orderPrice.toFixed(2)} USDC/BTC`,
                orderLeverage: orderLeverage.toFixed(2),
                calculation: `(${orderQuantity.toFixed(2)} BTC * ${orderPrice.toFixed(2)} USDC/BTC) / ${orderLeverage.toFixed(2)} = ${expectedMarginReserved.toFixed(2)} USDC`,
                note: "Using available margin from Step 2.1 as baseline (most recent before order placement)",
              },
              timestamp: Date.now(),
            });

            if (!marginReservedValid) {
              addResult(section, {
                success: false,
                message: `Bucket balance change mismatch: Expected margin reserved ${expectedMarginReserved.toFixed(2)} USDC, actual change ${bucketBalanceChange.toFixed(2)} USDC`,
                error: `Expected available margin to decrease by ${expectedMarginReserved.toFixed(2)} USDC, but decreased by ${bucketBalanceChange.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            // Final summary
            addResult(section, {
              success: marginReservedValid,
              message: `Place order validation test ${marginReservedValid ? "COMPLETED SUCCESSFULLY" : "COMPLETED WITH VALIDATION ERRORS"}`,
              data: {
                orderId,
                marketId: btcUsdcPerpMarketId,
                orderQuantity: `${orderQuantity.toFixed(2)} BTC`,
                orderLeverage: `${orderLeverage}x`,
                orderPrice: `${orderPrice.toFixed(2)} USDC`,
                availableMarginBeforeOrder: `${currentAvailableMargin.toFixed(2)} USDC`,
                availableMarginAfterOrder: `${bucketBalanceAfter.toFixed(2)} USDC`,
                marginReserved: `${bucketBalanceChange.toFixed(2)} USDC`,
                expectedMarginReserved: `${expectedMarginReserved.toFixed(2)} USDC`,
                validationPassed: marginReservedValid,
              },
              timestamp: Date.now(),
            });
          }
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "Place order validation test failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      }

      // Test placeOrder (submit order) - requires marketId and bucketId
      if (currentMarketId) {
        // Get bucketId from first available bucket
        if (!hookBuckets || hookBuckets.length === 0) {
          addResult(section, {
            success: false,
            message: "placeOrder() - No buckets available. Please create a bucket first.",
            error: "Bucket ID is required for order placement",
            timestamp: Date.now(),
          });
          return;
        }
        const firstBucket = hookBuckets[0];
        const bucketId = firstBucket.bucketId;
        if (!bucketId) {
          addResult(section, {
            success: false,
            message: "placeOrder() - Bucket ID not found in bucket data",
            error: "Bucket ID is required for order placement",
            timestamp: Date.now(),
          });
          return;
        }

        // Prepare order request body
        // Price and quantity are decimal numbers (converted to satoshi in buildOrderPayload)
        // Leverage needs to be converted to satoshi format (following working example from TradeOperation.tsx)
        const leverage = 1.0; // Default leverage
        const leverageStr = toSatoshi(leverage.toString());
        const orderRequest = {
          marketId: marketIdString,
          bucketId: bucketId,
          side: "buy" as const,
          orderType: "LIMIT" as const,
          price: 40010.8778, // Decimal number (converted to satoshi string in buildOrderPayload)
          quantity: 0.01, // Decimal number (converted to satoshi string in buildOrderPayload)
          timeInForce: "GTC" as const,
          leverage: leverageStr, // Leverage in satoshi format (e.g., "100000000" for 1.0)
        };

        addResult(section, {
          success: true,
          message: `Testing placeOrder() (submit order) for market ${currentMarketId}...`,
          data: {
            requestBody: orderRequest,
            endpoint: "POST /clob/v1/order",
            note: "Price and quantity are decimal numbers, converted to satoshi strings in buildOrderPayload(). Leverage is already in satoshi format.",
            leverage: {
              decimal: leverage,
              satoshi: leverageStr,
            },
          },
          timestamp: Date.now(),
        });
        try {
          // Using market ID from page
          // Place a limit buy order with small quantity for testing
          const orderResult =
            await exchangeClientRef.current.placeOrder(orderRequest);

          // Extract orderId from response (PlaceOrderResponse.order_id)
          if (orderResult) {
            placedOrderId = orderResult.order_id || null;
            if (placedOrderId) {
              addResult(section, {
                success: true,
                message: `placeOrder() - POST /clob/v1/order executed successfully. Order ID: ${placedOrderId.substring(0, 16)}...`,
                data: {
                  orderResult,
                  orderId: placedOrderId,
                  requestBody: orderRequest,
                  endpoint: "POST /clob/v1/order",
                },
                timestamp: Date.now(),
              });
            } else {
              addResult(section, {
                success: true,
                message:
                  "placeOrder() - POST /clob/v1/order executed successfully (orderId not found in response)",
                data: {
                  orderResult,
                  requestBody: orderRequest,
                  endpoint: "POST /clob/v1/order",
                },
                timestamp: Date.now(),
              });
            }
          } else {
            addResult(section, {
              success: false,
              message:
                "placeOrder() - POST /clob/v1/order returned empty response",
              data: {
                requestBody: orderRequest,
                endpoint: "POST /clob/v1/order",
              },
              timestamp: Date.now(),
            });
          }
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "placeOrder() - POST /clob/v1/order failed",
            error: error.message,
            data: {
              requestBody: orderRequest,
              endpoint: "POST /clob/v1/order",
            },
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping placeOrder() test: No market ID available",
          timestamp: Date.now(),
        });
      }

      // Test cancelOrder - requires orderId and marketId
      if (currentMarketId && placedOrderId) {
        addResult(section, {
          success: true,
          message: `Testing cancelOrder() for order ${placedOrderId.substring(0, 16)}...`,
          timestamp: Date.now(),
        });
        try {
          // Using market ID and orderId from placeOrder
          const cancelResult = await exchangeClientRef.current.cancelOrder({
            orderId: placedOrderId,
            marketId: marketIdString,
          });
          addResult(section, {
            success: !!cancelResult,
            message: "cancelOrder() - POST /router/v1/tx executed successfully",
            data: cancelResult,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "cancelOrder() - POST /router/v1/tx failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      } else {
        if (!currentMarketId) {
          addResult(section, {
            success: true,
            message: "Skipping cancelOrder() test: No market ID available",
            timestamp: Date.now(),
          });
        } else if (!placedOrderId) {
          addResult(section, {
            success: true,
            message:
              "Skipping cancelOrder() test: No order ID available (placeOrder may have failed or orderId not in response)",
            timestamp: Date.now(),
          });
        }
      }

      // Test closePosition (requires marketId and optionally marketPrice/quantity)
      if (currentMarketId) {
        addResult(section, {
          success: true,
          message: `Testing closePosition() for market ${currentMarketId}...`,
          timestamp: Date.now(),
        });
        try {
          // First, check if there's an open position by querying positions
          let hasPosition = false;
          let positionMarketPrice = 0;
          let positionId: string | undefined;
          try {
            if (!infoClientRef.current) {
              infoClientRef.current = new InfoClient({
                baseURL: baseURLRef.current,
              });
            }
            if (sessionToken && infoClientRef.current) {
              infoClientRef.current.setSessionToken(sessionToken);
            }
            const positions =
              await infoClientRef.current.getPositions(walletAddr);
            const positionForMarket = Array.isArray(positions)
              ? positions.find(
                  (p: any) => (p.market_id || p.marketId) === currentMarketId,
                )
              : null;

            if (positionForMarket) {
              hasPosition = true;
              // Extract position ID
              positionId = (positionForMarket as any).position_id || (positionForMarket as any).positionId;
              // Get mark price from position or fetch from ticker
              if (positionForMarket.mark_price) {
                positionMarketPrice = fromSatoshi(
                  positionForMarket.mark_price || "0",
                );
              } else {
                // Fetch from ticker
                const ticker =
                  await infoClientRef.current.getTicker(currentMarketId);
                if (ticker && ticker.last_price) {
                  positionMarketPrice = fromSatoshi(
                    typeof ticker.last_price === "string"
                      ? ticker.last_price
                      : String(ticker.last_price),
                  );
                }
              }
            }
          } catch (error: any) {
            addResult(section, {
              success: true,
              message: `Position check failed (will attempt close anyway): ${error.message}`,
              timestamp: Date.now(),
            });
          }

          if (!hasPosition) {
            addResult(section, {
              success: true,
              message:
                "No open position found for this market. Skipping closePosition() test.",
              data: {
                marketId: currentMarketId,
                note: "closePosition() requires an open position to test",
              },
              timestamp: Date.now(),
            });
          } else {
            // Using market ID from page
            // marketPrice and quantity are optional - if not provided, will fetch price and close full position
            if (!positionId) {
              addResult(section, {
                success: false,
                message: "closePosition() - positionId is required but not found in position data",
                timestamp: Date.now(),
              });
            } else {
              const closeResult = await exchangeClientRef.current.closePosition({
                positionId: positionId,
                marketId: marketIdString,
                marketPrice:
                  positionMarketPrice > 0 ? positionMarketPrice : undefined, // Optional: will be fetched if not provided
                quantity: undefined, // Optional: undefined or 0 means close full position
              });
              addResult(section, {
                success: !!closeResult,
                message:
                  "closePosition() - POST /bucket/v1/position/close executed successfully",
                data: {
                  closeResult,
                  note: "quantity=undefined means close full position (closeSize=0 in request)",
                },
                timestamp: Date.now(),
              });
            }
          }
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "closePosition() - POST /bucket/v1/position/close failed (may not have open position)",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping closePosition() test: No market ID available",
          timestamp: Date.now(),
        });
      }

      // Test bucket operations - need to get buckets first
      addResult(section, {
        success: true,
        message: "Fetching buckets for bucket operation tests...",
        timestamp: Date.now(),
      });

      // Get buckets using InfoClient
      let availableBuckets: any[] = [];
      try {
        // Recreate InfoClient if needed
        if (!infoClientRef.current) {
          infoClientRef.current = new InfoClient({
            baseURL: baseURLRef.current,
          });
        }
        if (sessionToken && infoClientRef.current) {
          infoClientRef.current.setSessionToken(sessionToken);
        }

        const buckets = await infoClientRef.current.getBuckets(walletAddr);
        availableBuckets = Array.isArray(buckets) ? buckets : [];

        addResult(section, {
          success: true,
          message: `Found ${availableBuckets.length} bucket(s) for testing`,
          data: {
            bucketCount: availableBuckets.length,
            buckets: availableBuckets.slice(0, 3).map((b: any) => ({
              bucketId: b.bucket_id || b.id || b.bucketId,
              availableMargin: b.available_margin || b.availableMargin,
            })),
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "Failed to fetch buckets for testing",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test extractToBucket (bank → bucket)
      if (availableBuckets.length > 0) {
        const testBucket = availableBuckets[0];
        const testBucketId =
          testBucket.bucket_id || testBucket.id || testBucket.bucketId;

        if (testBucketId) {
          addResult(section, {
            success: true,
            message: `Testing extractToBucket() (1 USD = 100000000 satoshi)...`,
            timestamp: Date.now(),
          });
          try {
            const extractResult =
              await exchangeClientRef.current.extractToBucket({
                bucketId: testBucketId,
                assetId: "USDC",
                amount: 1, // 1 USD (will be converted to satoshi internally)
              });
            addResult(section, {
              success: !!extractResult,
              message:
                "extractToBucket() - POST /bank/v1/extract-to-bucket executed successfully",
              data: extractResult,
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message:
                "extractToBucket() - POST /bank/v1/extract-to-bucket failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message:
              "Skipping extractToBucket() test: No valid bucket ID found",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping extractToBucket() test: No buckets available",
          timestamp: Date.now(),
        });
      }

      // Test withdrawFromBucket (bucket → bank) with validation
      if (availableBuckets.length > 0) {
        const testBucket = availableBuckets[0];
        const testBucketId =
          testBucket.bucket_id || testBucket.id || testBucket.bucketId;

        if (testBucketId) {
          addResult(section, {
            success: true,
            message:
              "Testing bucket to spot wallet transfer (10.00 USDC) with validation...",
            timestamp: Date.now(),
          });

          try {
            // Step 1: Load bucket balance before transfer
            addResult(section, {
              success: true,
              message: "Step 1: Loading bucket USDC balance before transfer...",
              timestamp: Date.now(),
            });

            let bucketBalanceBefore: number = 0;
            try {
              if (!infoClientRef.current) {
                infoClientRef.current = new InfoClient({
                  baseURL: baseURLRef.current,
                });
              }
              if (sessionToken && infoClientRef.current) {
                infoClientRef.current.setSessionToken(sessionToken);
              }
              const bucketResponse =
                await infoClientRef.current.getBucket(testBucketId);
              bucketBalanceBefore = fromSatoshi(
                bucketResponse.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 1: Bucket USDC balance before: ${bucketBalanceBefore.toFixed(2)} USDC`,
                data: {
                  balance: bucketBalanceBefore,
                  raw: bucketResponse,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 1: Failed to load bucket balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 2: Load wallet balance before transfer
            addResult(section, {
              success: true,
              message:
                "Step 2: Loading spot wallet USDC balance before transfer...",
              timestamp: Date.now(),
            });

            let walletBalanceBefore: number = 0;
            try {
              const walletBalanceResponse =
                await infoClientRef.current.getBalanceDecimal(
                  walletAddr,
                  "USDC",
                );
              walletBalanceBefore = fromSatoshi(
                walletBalanceResponse.available_balance || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 2: Spot wallet USDC balance before: ${walletBalanceBefore.toFixed(2)} USDC`,
                data: {
                  balance: walletBalanceBefore,
                  raw: walletBalanceResponse,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 2: Failed to load spot wallet balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 3: Perform transfer (10.00 USDC)
            const transferAmount = 10.0;
            const transferAmountSatoshi = toSatoshi(transferAmount.toString());

            addResult(section, {
              success: true,
              message: `Step 3: Transferring ${transferAmount.toFixed(2)} USDC (${transferAmountSatoshi} satoshi) from bucket to spot wallet...`,
              timestamp: Date.now(),
            });

            try {
              const withdrawResult =
                await exchangeClientRef.current.withdrawFromBucket({
                  bucketId: testBucketId,
                  assetId: "USDC",
                  amount: transferAmount, // Decimal number (will be converted to satoshi internally)
                });
              addResult(section, {
                success: !!withdrawResult,
                message: `Step 3: Transfer executed successfully`,
                data: withdrawResult,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 3: Transfer failed",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Wait a bit for the transfer to be processed
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Step 4: Load bucket balance after transfer
            addResult(section, {
              success: true,
              message: "Step 4: Loading bucket USDC balance after transfer...",
              timestamp: Date.now(),
            });

            let bucketBalanceAfter: number = 0;
            try {
              const bucketResponseAfter =
                await infoClientRef.current.getBucket(testBucketId);
              bucketBalanceAfter = fromSatoshi(
                bucketResponseAfter.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 4: Bucket USDC balance after: ${bucketBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: bucketBalanceAfter,
                  raw: bucketResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 4: Failed to load bucket balance after transfer",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 5: Load wallet balance after transfer
            addResult(section, {
              success: true,
              message:
                "Step 5: Loading spot wallet USDC balance after transfer...",
              timestamp: Date.now(),
            });

            let walletBalanceAfter: number = 0;
            try {
              const walletBalanceResponseAfter =
                await infoClientRef.current.getBalanceDecimal(
                  walletAddr,
                  "USDC",
                );
              walletBalanceAfter = fromSatoshi(
                walletBalanceResponseAfter.available_balance || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 5: Spot wallet USDC balance after: ${walletBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: walletBalanceAfter,
                  raw: walletBalanceResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message:
                  "Step 5: Failed to load spot wallet balance after transfer",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 6: Validate the transfer amounts
            addResult(section, {
              success: true,
              message: "Step 6: Validating transfer amounts...",
              timestamp: Date.now(),
            });

            const bucketBalanceDecrease =
              bucketBalanceBefore - bucketBalanceAfter;
            const walletBalanceIncrease =
              walletBalanceAfter - walletBalanceBefore;
            const expectedDecrease = transferAmount;
            const expectedIncrease = transferAmount;

            const bucketDecreaseValid =
              Math.abs(bucketBalanceDecrease - expectedDecrease) < 0.01;
            const walletIncreaseValid =
              Math.abs(walletBalanceIncrease - expectedIncrease) < 0.01;

            addResult(section, {
              success: bucketDecreaseValid && walletIncreaseValid,
              message: `Step 6: Validation ${bucketDecreaseValid && walletIncreaseValid ? "PASSED" : "FAILED"}`,
              data: {
                bucketBalanceBefore: bucketBalanceBefore.toFixed(2),
                bucketBalanceAfter: bucketBalanceAfter.toFixed(2),
                bucketBalanceDecrease: bucketBalanceDecrease.toFixed(2),
                expectedBucketDecrease: expectedDecrease.toFixed(2),
                bucketDecreaseValid,
                walletBalanceBefore: walletBalanceBefore.toFixed(2),
                walletBalanceAfter: walletBalanceAfter.toFixed(2),
                walletBalanceIncrease: walletBalanceIncrease.toFixed(2),
                expectedWalletIncrease: expectedIncrease.toFixed(2),
                walletIncreaseValid,
                transferAmount: transferAmount.toFixed(2),
              },
              timestamp: Date.now(),
            });

            if (!bucketDecreaseValid) {
              addResult(section, {
                success: false,
                message: `Validation failed: Bucket balance decrease (${bucketBalanceDecrease.toFixed(2)}) does not match expected (${expectedDecrease.toFixed(2)})`,
                error: `Expected bucket to decrease by ${expectedDecrease.toFixed(2)} USDC, but decreased by ${bucketBalanceDecrease.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            if (!walletIncreaseValid) {
              addResult(section, {
                success: false,
                message: `Validation failed: Wallet balance increase (${walletBalanceIncrease.toFixed(2)}) does not match expected (${expectedIncrease.toFixed(2)})`,
                error: `Expected wallet to increase by ${expectedIncrease.toFixed(2)} USDC, but increased by ${walletBalanceIncrease.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            // Final summary
            addResult(section, {
              success: bucketDecreaseValid && walletIncreaseValid,
              message: `Bucket to spot wallet transfer test ${bucketDecreaseValid && walletIncreaseValid ? "COMPLETED SUCCESSFULLY" : "COMPLETED WITH VALIDATION ERRORS"}`,
              data: {
                transferAmount: `${transferAmount.toFixed(2)} USDC`,
                bucketBalanceBefore: `${bucketBalanceBefore.toFixed(2)} USDC`,
                bucketBalanceAfter: `${bucketBalanceAfter.toFixed(2)} USDC`,
                bucketBalanceChange: `${bucketBalanceDecrease.toFixed(2)} USDC`,
                walletBalanceBefore: `${walletBalanceBefore.toFixed(2)} USDC`,
                walletBalanceAfter: `${walletBalanceAfter.toFixed(2)} USDC`,
                walletBalanceChange: `${walletBalanceIncrease.toFixed(2)} USDC`,
                validationPassed: bucketDecreaseValid && walletIncreaseValid,
              },
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: "Bucket to spot wallet transfer test failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message:
              "Skipping bucket to spot wallet transfer test: No valid bucket ID found",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "Skipping bucket to spot wallet transfer test: No buckets available",
          timestamp: Date.now(),
        });
      }

      // Test bucketTransfer (bucket → bucket) with validation
      if (availableBuckets.length >= 2) {
        const sourceBucket = availableBuckets[0];
        const targetBucket = availableBuckets[1];
        const sourceBucketId =
          sourceBucket.bucket_id || sourceBucket.id || sourceBucket.bucketId;
        const targetBucketId =
          targetBucket.bucket_id || targetBucket.id || targetBucket.bucketId;

        if (sourceBucketId && targetBucketId) {
          addResult(section, {
            success: true,
            message:
              "Testing bucket to bucket transfer (10.00 USDC) with validation...",
            timestamp: Date.now(),
          });

          try {
            // Step 1: Load source bucket balance before transfer
            addResult(section, {
              success: true,
              message:
                "Step 1: Loading source bucket USDC balance before transfer...",
              timestamp: Date.now(),
            });

            let sourceBucketBalanceBefore: number = 0;
            try {
              if (!infoClientRef.current) {
                infoClientRef.current = new InfoClient({
                  baseURL: baseURLRef.current,
                });
              }
              if (sessionToken && infoClientRef.current) {
                infoClientRef.current.setSessionToken(sessionToken);
              }
              const sourceBucketResponse =
                await infoClientRef.current.getBucket(sourceBucketId);
              sourceBucketBalanceBefore = fromSatoshi(
                sourceBucketResponse.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 1: Source bucket USDC balance before: ${sourceBucketBalanceBefore.toFixed(2)} USDC`,
                data: {
                  balance: sourceBucketBalanceBefore,
                  raw: sourceBucketResponse,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 1: Failed to load source bucket balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 2: Load target bucket balance before transfer
            addResult(section, {
              success: true,
              message:
                "Step 2: Loading target bucket USDC balance before transfer...",
              timestamp: Date.now(),
            });

            let targetBucketBalanceBefore: number = 0;
            try {
              const targetBucketResponse =
                await infoClientRef.current.getBucket(targetBucketId);
              targetBucketBalanceBefore = fromSatoshi(
                targetBucketResponse.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 2: Target bucket USDC balance before: ${targetBucketBalanceBefore.toFixed(2)} USDC`,
                data: {
                  balance: targetBucketBalanceBefore,
                  raw: targetBucketResponse,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 2: Failed to load target bucket balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 3: Perform transfer (10.00 USDC)
            const transferAmount = 10.0;
            const transferAmountSatoshi = toSatoshi(transferAmount.toString());

            addResult(section, {
              success: true,
              message: `Step 3: Transferring ${transferAmount.toFixed(2)} USDC (${transferAmountSatoshi} satoshi) from source bucket to target bucket...`,
              timestamp: Date.now(),
            });

            try {
              const transferResult =
                await exchangeClientRef.current.bucketTransfer({
                  fromBucketId: sourceBucketId,
                  toBucketId: targetBucketId,
                  assetId: "USDC",
                  amount: transferAmount, // Decimal number (will be converted to satoshi internally)
                });
              addResult(section, {
                success: !!transferResult,
                message: `Step 3: Transfer executed successfully`,
                data: transferResult,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 3: Transfer failed",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Wait a bit for the transfer to be processed
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Step 4: Load source bucket balance after transfer
            addResult(section, {
              success: true,
              message:
                "Step 4: Loading source bucket USDC balance after transfer...",
              timestamp: Date.now(),
            });

            let sourceBucketBalanceAfter: number = 0;
            try {
              const sourceBucketResponseAfter =
                await infoClientRef.current.getBucket(sourceBucketId);
              sourceBucketBalanceAfter = fromSatoshi(
                sourceBucketResponseAfter.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 4: Source bucket USDC balance after: ${sourceBucketBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: sourceBucketBalanceAfter,
                  raw: sourceBucketResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message:
                  "Step 4: Failed to load source bucket balance after transfer",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 5: Load target bucket balance after transfer
            addResult(section, {
              success: true,
              message:
                "Step 5: Loading target bucket USDC balance after transfer...",
              timestamp: Date.now(),
            });

            let targetBucketBalanceAfter: number = 0;
            try {
              const targetBucketResponseAfter =
                await infoClientRef.current.getBucket(targetBucketId);
              targetBucketBalanceAfter = fromSatoshi(
                targetBucketResponseAfter.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 5: Target bucket USDC balance after: ${targetBucketBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: targetBucketBalanceAfter,
                  raw: targetBucketResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message:
                  "Step 5: Failed to load target bucket balance after transfer",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 6: Validate the transfer amounts
            addResult(section, {
              success: true,
              message: "Step 6: Validating transfer amounts...",
              timestamp: Date.now(),
            });

            const sourceBucketBalanceDecrease =
              sourceBucketBalanceBefore - sourceBucketBalanceAfter;
            const targetBucketBalanceIncrease =
              targetBucketBalanceAfter - targetBucketBalanceBefore;
            const expectedDecrease = transferAmount;
            const expectedIncrease = transferAmount;

            const sourceDecreaseValid =
              Math.abs(sourceBucketBalanceDecrease - expectedDecrease) < 0.01;
            const targetIncreaseValid =
              Math.abs(targetBucketBalanceIncrease - expectedIncrease) < 0.01;

            addResult(section, {
              success: sourceDecreaseValid && targetIncreaseValid,
              message: `Step 6: Validation ${sourceDecreaseValid && targetIncreaseValid ? "PASSED" : "FAILED"}`,
              data: {
                sourceBucketBalanceBefore: sourceBucketBalanceBefore.toFixed(2),
                sourceBucketBalanceAfter: sourceBucketBalanceAfter.toFixed(2),
                sourceBucketBalanceDecrease:
                  sourceBucketBalanceDecrease.toFixed(2),
                expectedSourceDecrease: expectedDecrease.toFixed(2),
                sourceDecreaseValid,
                targetBucketBalanceBefore: targetBucketBalanceBefore.toFixed(2),
                targetBucketBalanceAfter: targetBucketBalanceAfter.toFixed(2),
                targetBucketBalanceIncrease:
                  targetBucketBalanceIncrease.toFixed(2),
                expectedTargetIncrease: expectedIncrease.toFixed(2),
                targetIncreaseValid,
                transferAmount: transferAmount.toFixed(2),
              },
              timestamp: Date.now(),
            });

            if (!sourceDecreaseValid) {
              addResult(section, {
                success: false,
                message: `Validation failed: Source bucket balance decrease (${sourceBucketBalanceDecrease.toFixed(2)}) does not match expected (${expectedDecrease.toFixed(2)})`,
                error: `Expected source bucket to decrease by ${expectedDecrease.toFixed(2)} USDC, but decreased by ${sourceBucketBalanceDecrease.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            if (!targetIncreaseValid) {
              addResult(section, {
                success: false,
                message: `Validation failed: Target bucket balance increase (${targetBucketBalanceIncrease.toFixed(2)}) does not match expected (${expectedIncrease.toFixed(2)})`,
                error: `Expected target bucket to increase by ${expectedIncrease.toFixed(2)} USDC, but increased by ${targetBucketBalanceIncrease.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            // Final summary
            addResult(section, {
              success: sourceDecreaseValid && targetIncreaseValid,
              message: `Bucket to bucket transfer test ${sourceDecreaseValid && targetIncreaseValid ? "COMPLETED SUCCESSFULLY" : "COMPLETED WITH VALIDATION ERRORS"}`,
              data: {
                transferAmount: `${transferAmount.toFixed(2)} USDC`,
                sourceBucketBalanceBefore: `${sourceBucketBalanceBefore.toFixed(2)} USDC`,
                sourceBucketBalanceAfter: `${sourceBucketBalanceAfter.toFixed(2)} USDC`,
                sourceBucketBalanceChange: `${sourceBucketBalanceDecrease.toFixed(2)} USDC`,
                targetBucketBalanceBefore: `${targetBucketBalanceBefore.toFixed(2)} USDC`,
                targetBucketBalanceAfter: `${targetBucketBalanceAfter.toFixed(2)} USDC`,
                targetBucketBalanceChange: `${targetBucketBalanceIncrease.toFixed(2)} USDC`,
                validationPassed: sourceDecreaseValid && targetIncreaseValid,
              },
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: "Bucket to bucket transfer test failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message:
              "Skipping bucket to bucket transfer test: Invalid bucket IDs",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "Skipping bucket to bucket transfer test: Need at least 2 buckets (found " +
            availableBuckets.length +
            ")",
          timestamp: Date.now(),
        });
      }

      // Test spot wallet to bucket default transfer with validation
      if (availableBuckets.length > 0) {
        const defaultBucket = availableBuckets[0];
        const defaultBucketId =
          defaultBucket.bucket_id || defaultBucket.id || defaultBucket.bucketId;

        if (defaultBucketId) {
          addResult(section, {
            success: true,
            message:
              "Testing spot wallet to bucket default transfer (10.00 USDC) with validation...",
            timestamp: Date.now(),
          });

          try {
            // Step 1: Load wallet balance before transfer
            addResult(section, {
              success: true,
              message:
                "Step 1: Loading spot wallet USDC balance before transfer...",
              timestamp: Date.now(),
            });

            let walletBalanceBefore: number = 0;
            try {
              if (!infoClientRef.current) {
                infoClientRef.current = new InfoClient({
                  baseURL: baseURLRef.current,
                });
              }
              if (sessionToken && infoClientRef.current) {
                infoClientRef.current.setSessionToken(sessionToken);
              }
              if (!infoClientRef.current) {
                throw new Error("Failed to initialize InfoClient");
              }
              const walletBalanceResponse =
                await infoClientRef.current.getBalanceDecimal(
                  walletAddr,
                  "USDC",
                );
              walletBalanceBefore = fromSatoshi(
                walletBalanceResponse.available_balance || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 1: Spot wallet USDC balance before: ${walletBalanceBefore.toFixed(2)} USDC`,
                data: {
                  balance: walletBalanceBefore,
                  raw: walletBalanceResponse,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 1: Failed to load spot wallet balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 2: Load bucket balance before transfer
            addResult(section, {
              success: true,
              message:
                "Step 2: Loading default bucket USDC balance before transfer...",
              timestamp: Date.now(),
            });

            let bucketBalanceBefore: number = 0;
            try {
              if (!infoClientRef.current) {
                infoClientRef.current = new InfoClient({
                  baseURL: baseURLRef.current,
                });
              }
              if (sessionToken && infoClientRef.current) {
                infoClientRef.current.setSessionToken(sessionToken);
              }
              const bucketResponse =
                await infoClientRef.current.getBucket(defaultBucketId);
              bucketBalanceBefore = fromSatoshi(
                bucketResponse.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 2: Default bucket USDC balance before: ${bucketBalanceBefore.toFixed(2)} USDC`,
                data: {
                  balance: bucketBalanceBefore,
                  raw: bucketResponse,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 2: Failed to load bucket balance",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 3: Perform transfer (10.00 USDC)
            const transferAmount = 10.0;
            const transferAmountSatoshi = toSatoshi(transferAmount.toString());

            addResult(section, {
              success: true,
              message: `Step 3: Transferring ${transferAmount.toFixed(2)} USDC (${transferAmountSatoshi} satoshi) from spot wallet to default bucket...`,
              timestamp: Date.now(),
            });

            try {
              const transferResult =
                await exchangeClientRef.current.extractToBucket({
                  bucketId: defaultBucketId,
                  assetId: "USDC",
                  amount: transferAmount, // Decimal number (will be converted to satoshi internally)
                });
              addResult(section, {
                success: !!transferResult,
                message: `Step 3: Transfer executed successfully`,
                data: transferResult,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 3: Transfer failed",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Wait a bit for the transfer to be processed
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Step 4: Load wallet balance after transfer
            addResult(section, {
              success: true,
              message:
                "Step 4: Loading spot wallet USDC balance after transfer...",
              timestamp: Date.now(),
            });

            let walletBalanceAfter: number = 0;
            try {
              const walletBalanceResponseAfter =
                await infoClientRef.current.getBalanceDecimal(
                  walletAddr,
                  "USDC",
                );
              walletBalanceAfter = fromSatoshi(
                walletBalanceResponseAfter.available_balance || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 4: Spot wallet USDC balance after: ${walletBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: walletBalanceAfter,
                  raw: walletBalanceResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message:
                  "Step 4: Failed to load spot wallet balance after transfer",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 5: Load bucket balance after transfer
            addResult(section, {
              success: true,
              message:
                "Step 5: Loading default bucket USDC balance after transfer...",
              timestamp: Date.now(),
            });

            let bucketBalanceAfter: number = 0;
            try {
              const bucketResponseAfter =
                await infoClientRef.current.getBucket(defaultBucketId);
              bucketBalanceAfter = fromSatoshi(
                bucketResponseAfter.available_margin || "0",
              );
              addResult(section, {
                success: true,
                message: `Step 5: Default bucket USDC balance after: ${bucketBalanceAfter.toFixed(2)} USDC`,
                data: {
                  balance: bucketBalanceAfter,
                  raw: bucketResponseAfter,
                },
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: "Step 5: Failed to load bucket balance after transfer",
                error: error.message,
                timestamp: Date.now(),
              });
              throw error;
            }

            // Step 6: Validate the transfer amounts
            addResult(section, {
              success: true,
              message: "Step 6: Validating transfer amounts...",
              timestamp: Date.now(),
            });

            const walletBalanceDecrease =
              walletBalanceBefore - walletBalanceAfter;
            const bucketBalanceIncrease =
              bucketBalanceAfter - bucketBalanceBefore;
            const expectedDecrease = transferAmount;
            const expectedIncrease = transferAmount;

            const walletDecreaseValid =
              Math.abs(walletBalanceDecrease - expectedDecrease) < 0.01;
            const bucketIncreaseValid =
              Math.abs(bucketBalanceIncrease - expectedIncrease) < 0.01;

            addResult(section, {
              success: walletDecreaseValid && bucketIncreaseValid,
              message: `Step 6: Validation ${walletDecreaseValid && bucketIncreaseValid ? "PASSED" : "FAILED"}`,
              data: {
                walletBalanceBefore: walletBalanceBefore.toFixed(2),
                walletBalanceAfter: walletBalanceAfter.toFixed(2),
                walletBalanceDecrease: walletBalanceDecrease.toFixed(2),
                expectedWalletDecrease: expectedDecrease.toFixed(2),
                walletDecreaseValid,
                bucketBalanceBefore: bucketBalanceBefore.toFixed(2),
                bucketBalanceAfter: bucketBalanceAfter.toFixed(2),
                bucketBalanceIncrease: bucketBalanceIncrease.toFixed(2),
                expectedBucketIncrease: expectedIncrease.toFixed(2),
                bucketIncreaseValid,
                transferAmount: transferAmount.toFixed(2),
              },
              timestamp: Date.now(),
            });

            if (!walletDecreaseValid) {
              addResult(section, {
                success: false,
                message: `Validation failed: Wallet balance decrease (${walletBalanceDecrease.toFixed(2)}) does not match expected (${expectedDecrease.toFixed(2)})`,
                error: `Expected wallet to decrease by ${expectedDecrease.toFixed(2)} USDC, but decreased by ${walletBalanceDecrease.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            if (!bucketIncreaseValid) {
              addResult(section, {
                success: false,
                message: `Validation failed: Bucket balance increase (${bucketBalanceIncrease.toFixed(2)}) does not match expected (${expectedIncrease.toFixed(2)})`,
                error: `Expected bucket to increase by ${expectedIncrease.toFixed(2)} USDC, but increased by ${bucketBalanceIncrease.toFixed(2)} USDC`,
                timestamp: Date.now(),
              });
            }

            // Final summary
            addResult(section, {
              success: walletDecreaseValid && bucketIncreaseValid,
              message: `Spot wallet to bucket transfer test ${walletDecreaseValid && bucketIncreaseValid ? "COMPLETED SUCCESSFULLY" : "COMPLETED WITH VALIDATION ERRORS"}`,
              data: {
                transferAmount: `${transferAmount.toFixed(2)} USDC`,
                walletBalanceBefore: `${walletBalanceBefore.toFixed(2)} USDC`,
                walletBalanceAfter: `${walletBalanceAfter.toFixed(2)} USDC`,
                walletBalanceChange: `${walletBalanceDecrease.toFixed(2)} USDC`,
                bucketBalanceBefore: `${bucketBalanceBefore.toFixed(2)} USDC`,
                bucketBalanceAfter: `${bucketBalanceAfter.toFixed(2)} USDC`,
                bucketBalanceChange: `${bucketBalanceIncrease.toFixed(2)} USDC`,
                validationPassed: walletDecreaseValid && bucketIncreaseValid,
              },
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: "Spot wallet to bucket transfer test failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message:
              "Skipping spot wallet to bucket transfer test: No valid default bucket ID found",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "Skipping spot wallet to bucket transfer test: No buckets available",
          timestamp: Date.now(),
        });
      }

      // Test createBucket (create new margin bucket)
      addResult(section, {
        success: true,
        message: "Testing createBucket() (create new margin bucket)...",
        timestamp: Date.now(),
      });

      try {
        // Step 1: Get current bucket count before creation
        addResult(section, {
          success: true,
          message: "Step 1: Getting current bucket count before creation...",
          timestamp: Date.now(),
        });

        let bucketsBefore: any[] = [];
        let bucketCountBefore = 0;
        try {
          if (!infoClientRef.current) {
            throw new Error("InfoClient not initialized");
          }
          bucketsBefore = await infoClientRef.current.getBuckets(walletAddr);
          bucketCountBefore = Array.isArray(bucketsBefore)
            ? bucketsBefore.length
            : 0;

          addResult(section, {
            success: true,
            message: `Step 1: Current bucket count: ${bucketCountBefore}`,
            data: {
              bucketCount: bucketCountBefore,
              buckets: Array.isArray(bucketsBefore)
                ? bucketsBefore.slice(0, 3).map((b: any) => ({
                    bucketId: b.bucket_id || b.id || b.bucketId,
                    marginType: b.margin_type || b.marginType,
                    collateralAsset: b.collateral_asset || b.collateralAsset,
                  }))
                : [],
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "Step 1: Failed to get current buckets",
            error: error.message,
            timestamp: Date.now(),
          });
          throw error;
        }

        // Step 2: Create cross margin USDC bucket
        addResult(section, {
          success: true,
          message: "Step 2: Creating cross margin USDC bucket...",
          timestamp: Date.now(),
        });

        let createResult: any = null;
        let createdBucketId: string | null = null;
        try {
          createResult = await exchangeClientRef.current.createBucket({
            bucketType: "USDC",
            marginType: "cross",
          });

          if (!createResult || !createResult.success) {
            throw new Error(createResult?.message || "Failed to create bucket");
          }

          createdBucketId = createResult.bucket_id || null;

          addResult(section, {
            success: !!createResult && !!createdBucketId,
            message: `Step 2: Cross margin USDC bucket created successfully. Bucket ID: ${createdBucketId?.substring(0, 16)}...`,
            data: {
              createResult,
              bucketId: createdBucketId,
              bucketType: "USDC",
              marginType: "cross",
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "Step 2: Failed to create cross margin USDC bucket",
            error: error.message,
            timestamp: Date.now(),
          });
          // Don't throw - continue with other tests
        }

        // Wait a bit for the bucket to be processed
        if (createdBucketId) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        // Step 3: Validate bucket was created by checking bucket list
        if (createdBucketId) {
          addResult(section, {
            success: true,
            message: "Step 3: Validating bucket was created...",
            timestamp: Date.now(),
          });

          try {
            if (!infoClientRef.current) {
              throw new Error("InfoClient not initialized");
            }
            const bucketsAfter =
              await infoClientRef.current.getBuckets(walletAddr);
            const bucketCountAfter = Array.isArray(bucketsAfter)
              ? bucketsAfter.length
              : 0;

            // Find the created bucket
            const createdBucket = Array.isArray(bucketsAfter)
              ? bucketsAfter.find(
                  (b: any) =>
                    (b.bucket_id || b.id || b.bucketId) === createdBucketId,
                )
              : null;

            const bucketFound = !!createdBucket;
            const bucketCountIncreased = bucketCountAfter > bucketCountBefore;

            addResult(section, {
              success: bucketFound && bucketCountIncreased,
              message: `Step 3: Bucket validation ${bucketFound && bucketCountIncreased ? "PASSED" : "FAILED"}`,
              data: {
                bucketCountBefore,
                bucketCountAfter,
                bucketCountIncreased,
                createdBucketId,
                bucketFound,
                createdBucket: createdBucket
                  ? {
                      bucketId: createdBucket.bucket_id,
                      marginType: createdBucket.margin_type,
                      collateralAsset: createdBucket.collateral_asset,
                    }
                  : null,
              },
              timestamp: Date.now(),
            });

            if (!bucketFound) {
              addResult(section, {
                success: false,
                message: `Validation failed: Created bucket (${createdBucketId?.substring(0, 16)}...) not found in bucket list`,
                error: "Bucket was created but not found in subsequent query",
                timestamp: Date.now(),
              });
            }

            if (!bucketCountIncreased) {
              addResult(section, {
                success: false,
                message: `Validation failed: Bucket count did not increase (before: ${bucketCountBefore}, after: ${bucketCountAfter})`,
                error: "Bucket count should increase after creation",
                timestamp: Date.now(),
              });
            }
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: "Step 3: Failed to validate bucket creation",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        }

        // Step 4: Test creating isolated margin USDC bucket
        addResult(section, {
          success: true,
          message: "Step 4: Testing isolated margin USDC bucket creation...",
          timestamp: Date.now(),
        });

        let isolatedBucketId: string | null = null;
        try {
          const isolatedResult = await exchangeClientRef.current.createBucket({
            bucketType: "USDC",
            marginType: "isolated",
          });

          if (!isolatedResult || !isolatedResult.success) {
            throw new Error(
              isolatedResult?.message || "Failed to create isolated bucket",
            );
          }

          isolatedBucketId = isolatedResult.bucket_id || null;

          addResult(section, {
            success: !!isolatedResult && !!isolatedBucketId,
            message: `Step 4: Isolated margin USDC bucket created successfully. Bucket ID: ${isolatedBucketId?.substring(0, 16)}...`,
            data: {
              createResult: isolatedResult,
              bucketId: isolatedBucketId,
              bucketType: "USDC",
              marginType: "isolated",
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "Step 4: Failed to create isolated margin USDC bucket",
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Final summary for createBucket tests
        addResult(section, {
          success: true,
          message: `Create bucket test ${createdBucketId && isolatedBucketId ? "COMPLETED SUCCESSFULLY" : "COMPLETED WITH SOME FAILURES"}`,
          data: {
            crossMarginBucketId: createdBucketId,
            isolatedMarginBucketId: isolatedBucketId,
            bucketCountBefore,
            testsPerformed: [
              "createBucket (cross margin USDC)",
              "createBucket (isolated margin USDC)",
            ],
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "Create bucket test failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Summary
      addResult(section, {
        success: true,
        message: "ExchangeClient tests completed",
        data: {
          walletAddress: walletAddr,
          marketId: marketIdString,
          sessionToken: sessionToken ? "present" : "not set",
          authToken: authToken ? "present" : "not set",
          methodsTested: [
            "placeOrder",
            "cancelOrder",
            "closePosition",
            "extractToBucket",
            "withdrawFromBucket",
            "bucketTransfer",
            "createBucket",
          ],
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "ExchangeClient test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // Run all tests
  const runAllTests = async () => {
    // Prevent concurrent test runs
    if (isRunning) {
      return;
    }

    setIsRunning(true);
    clearResults();
    toast.info("Running tests", {
      description: "Starting comprehensive SDK test suite...",
    });

    try {
      // Test utilities (synchronous)
      testUtilities();

      // Test InfoClient
      await testInfoClient();

      // Test Authentication (onboarding)
      await testAuthentication("Authentication");

      // Test Wallet Specific endpoints
      await testWalletSpecific();

      // Test ExchangeClient (if mnemonic provided)
      if (mnemonic) {
        await testExchangeClient();
      }

      // Completion toast will be shown by useEffect when isRunning becomes false
    } catch (error: any) {
      addResult("General", {
        success: false,
        message: "Test suite error",
        error: error.message,
        timestamp: Date.now(),
      });
      toast.error("Test suite error", {
        description:
          error.message || "An unexpected error occurred during testing.",
        duration: 6000,
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="App-content-row">
      <h1>SDK QA</h1>
      <p>Comprehensive test suite for all SDK APIs</p>

      {/* Configuration */}
      <div className="App-section">
        <h2>Configuration</h2>
        <div className="sdk-test-config-grid">
          <div>
            <label>
              HTTP Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Market:
              <select
                value={marketId || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    setMarketId(val); // Store as string
                  }
                }}
                className="sdk-test-config-select"
              >
                {markets.length === 0 ? (
                  <option value="">Loading markets...</option>
                ) : (
                  <>
                    <option value="" disabled>
                      -- Select a market --
                    </option>
                    {markets.map((market) => {
                      // Use exact market_id (string) from API, fallback to id converted to string
                      const marketIdValue =
                        market.market_id ?? market.id?.toString() ?? "";
                      // Prioritize moniker (from API), then symbol, then fallback
                      const displayName = market.moniker
                        ? market.moniker
                        : market.symbol
                          ? market.symbol
                          : market.baseAsset && market.quoteAsset
                            ? `${market.baseAsset}-${market.quoteAsset}`
                            : `Market ID: ${marketIdValue}`;
                      const isActive = market.active ? " [ACTIVE]" : "";
                      // Show truncated ID to avoid UI overflow

                      return (
                        <option key={marketIdValue} value={marketIdValue}>
                          {displayName} - ID: {marketIdValue}
                          {isActive}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
              {markets.length > 0 && (
                <small
                  className={`sdk-test-config-hint ${
                    marketId === defaultMarketId ? "info" : "warning"
                  }`}
                >
                  {marketId === defaultMarketId
                    ? `✓ Auto-selected default market (ID: ${defaultMarketId?.substring(0, 16)}...)`
                    : `⚠ Manual selection (ID: ${marketId?.substring(0, 16)}...)`}
                </small>
              )}
            </label>
          </div>
          <div>
            <label>
              Mnemonic (for ExchangeClient):
              <input
                type="text"
                value={mnemonic || ""}
                onChange={(e) => setOnboardMnemonic(e.target.value)}
                placeholder="Optional"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Session Token (for authenticated InfoClient):
              <input
                type="text"
                value={sessionToken}
                onChange={(e) => setSessionToken(e.target.value)}
                disabled={!!(sessionToken && sessionToken.trim() !== "")}
                placeholder="Optional"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Auth Token:
              <input
                type="text"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                disabled={
                  !!(onboardAuthToken && onboardAuthToken.trim() !== "")
                }
                placeholder={
                  onboardAuthToken
                    ? "Auto-filled from onboarding"
                    : 'Optional - Use "Onboard" button to generate'
                }
                className="sdk-test-config-input"
              />
              {onboardAuthToken && (
                <small className="sdk-test-config-hint success">
                  ✓ Auto-filled from onboarding
                </small>
              )}
            </label>
          </div>
          <div>
            <label>
              Wallet Address:
              <input
                type="text"
                value={walletAddress || ""}
                disabled={!!(mnemonic && mnemonic.trim() !== "")}
                onChange={() => {
                  // Wallet address is managed by useOnboard, but allow manual override via setAccountData if needed
                  // For now, just show it as read-only when mnemonic is set
                }}
                placeholder="automatic (from mnemonic)"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Device ID:
              <input
                type="text"
                value={deviceID}
                onChange={(e) => setDeviceID(e.target.value)}
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div className="control-box">
            <RunAllTestsButton isRunning={isRunning} onClick={runAllTests} />
            <OnboardButton
              mnemonic={mnemonic || ""}
              baseURL={baseURL}
              onboardLoading={isAuthTestRunning}
              isRunning={isRunning}
              onOnboard={async (mnemonic, baseURL) => {
                if (mnemonic && mnemonic.trim() !== "") {
                  setIsAuthTestRunning(true);
                  try {
                    await testAuthentication("Authentication");
                  } catch (error) {
                    console.error("Authentication test error:", error);
                  } finally {
                    setIsAuthTestRunning(false);
                  }
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to run authentication flow.",
                  });
                }
              }}
            />
            <UtilityTestButton
              isRunning={isRunning}
              isLoading={isUtilityTestRunning}
              onClick={() => {
                setIsUtilityTestRunning(true);
                try {
                  testUtilities();
                } catch (error) {
                  console.error("Utility test error:", error);
                } finally {
                  // Use setTimeout to ensure UI updates before clearing loading state
                  setTimeout(() => {
                    setIsUtilityTestRunning(false);
                  }, 100);
                }
              }}
            />
            <InfoClientTestButton
              isRunning={isRunning}
              isLoading={isInfoClientTestRunning}
              onClick={async () => {
                setIsInfoClientTestRunning(true);
                try {
                  await testInfoClient();
                } catch (error) {
                  console.error("Info Client test error:", error);
                } finally {
                  setIsInfoClientTestRunning(false);
                }
              }}
            />
            <WalletSpecificTestButton
              isRunning={isRunning}
              isLoading={isWalletSpecificTestRunning}
              onClick={async () => {
                setIsWalletSpecificTestRunning(true);
                try {
                  await testWalletSpecific();
                } catch (error) {
                  console.error("Wallet Specific test error:", error);
                } finally {
                  setIsWalletSpecificTestRunning(false);
                }
              }}
            />
            <ExchangeClientTestButton
              mnemonic={mnemonic || ""}
              isRunning={isRunning}
              isLoading={isExchangeClientTestRunning}
              onClick={async () => {
                if (mnemonic && mnemonic.trim() !== "") {
                  setIsExchangeClientTestRunning(true);
                  try {
                    await testExchangeClient();
                  } catch (error) {
                    console.error("Exchange Client test error:", error);
                  } finally {
                    setIsExchangeClientTestRunning(false);
                  }
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to run Exchange Client tests.",
                  });
                }
              }}
            />
            <ClearResultsButton isRunning={isRunning} onClick={clearResults} />
          </div>
        </div>

        {onboardAuthToken && (
          <span className="sdk-test-status success">
            ✓ Auth Token: {onboardAuthToken.substring(0, 20)}...
          </span>
        )}
        {onboardError && (
          <span className="sdk-test-status error">
            ❌ Onboard Error: {onboardError}
            <DismissErrorButton onClick={clearOnboardError} />
          </span>
        )}
      </div>

      {/* Results */}
      <div className="sdk-test-results">
        {Object.entries(sections).map(([sectionName, section]) => {
          const isExpanded = expandedSections.has(sectionName);
          return (
            <div key={sectionName} className="sdk-test-section">
              <SectionHeader
                sectionName={sectionName}
                isExpanded={isExpanded}
                onToggle={() => toggleSection(sectionName)}
              />
              {isExpanded && (
                <div className="sdk-test-section-content">
                  {section.results.map((result, index) => (
                    <TestResultItem key={index} result={result} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(sections).length === 0 && !isRunning && (
        <div className="sdk-test-empty-state">
          Click "Run All Tests" to start testing the SDK APIs
        </div>
      )}
    </div>
  );
}
